import { friendlyError } from '../../../utils/userMessages';
import { resolveCompanyId } from '../../../utils/companyIdentity';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../../utils/brandPalette';
import { getCompanyCoverUrl, getCompanyLogoUrl } from '../../../utils/companyIdentity';
// src/components/companies/CompaniesListing/CompaniesListing.js
// ============================================================================
// TelcoFair — Companies Directory with Invitations, Meetings & People Tabs
// Updated: Complete AI Introduction Request Handling with Notifications
// ============================================================================

import { openSafeExternalUrl } from '../../../utils/safeNavigation';
import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Avatar,
    IconButton,
    Tooltip,
    CircularProgress,
    Card,
    CardContent,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    LinearProgress,
    alpha,
    Tabs,
    Tab,
    Chip,
    Divider,
    Badge,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemSecondaryAction,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    Search as SearchIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    CheckCircle as CheckCircleIcon,
    Block as BlockIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    Visibility as VisibilityIcon,
    Verified as VerifiedIcon,
    TrendingUp as TrendingUpIcon,
    People as PeopleIcon,
    Public as PublicIcon,
    Close as ClearIcon,
    Chat as ChatIcon,
    Event as EventIcon,
    Send as SendIcon,
    Close as CloseIcon,
    Recommend as ThumbUpIcon,
    TrendingDown as ThumbDownIcon,
    Info as InfoIcon,
    FiberManualRecord as LiveIcon,
    AutoAwesome as AiIcon,
    Check as CheckIcon,
    NotificationsActive as NotificationIcon,
    Pending as PendingIcon,
    Link as LinkIcon,
    Email as InboxIcon,
    Stars as StarsIcon,
    Diamond as DiamondIcon,
    Handshake as HandshakeIcon,
    Image as ImageIcon,
    GraphicEq as SignalIcon,
    NetworkCheck as CellTowerIcon,
    SettingsApplications as HubIcon,
    NetworkCheck as RouterIcon,
    Cloud as CloudIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as SignalBarsIcon,
    Speed as SpeedIcon,
    Group as GroupIcon,
    Restore as HistoryIcon,
    ExpandMore as ExpandMoreIcon,
    Schedule as ScheduleIcon,
    CalendarToday as TodayIcon,
    Schedule as AccessTimeIcon,
    VideoCall as VideoCallIcon,
    LocationOn as LocationOnIcon2,
    NotesOutlined as NotesIcon,
    CalendarMonth as CalendarMonthIcon,
    EventNote as MeetingRoomIcon,
    Person as PersonIcon,
    BadgeOutlined as WorkIcon,
    Flag as FlagIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    LinkedIn as LinkedInIcon,
    Description as TitleIcon,
    ShoppingBagRounded as ShoppingCartIcon,
    PeopleAlt as PeopleAltIcon,
    Lock as LockIcon,
    Add as AddIcon,
    Inventory2 as CategoryIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../../config/firebase';
import {
    collection,
    getDocs,
    query,
    orderBy,
    where,
    doc,
    addDoc,
    serverTimestamp,
    updateDoc,
    getDoc,
    onSnapshot,
    deleteDoc,
    arrayUnion,
    arrayRemove,
} from 'firebase/firestore';
import toast from 'react-hot-toast';
import { safeExternalUrl } from '../../../utils/safeNavigation';
import Header from '../../common/Header/Header';
import { generateIntroduction } from '../../../services/integrations/ai';
import { introductionService, meetingWorkflowService } from '../../../services/workflow/communication';
import EntityRatingButton from '../../common/EntityRatingButton';

// ============================================
// PALETTE
// ============================================



const SHADOW = '25, 57, 78';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// ELEVATION LADDER
// ============================================================================

const ELEVATION = {
    0: { ambY: 2, ambB: 8, ambA: 0.05, conY: 1, conB: 2, conA: 0.04, blur: 8, rake: 0.5, fill: 0.34 },
    1: { ambY: 8, ambB: 24, ambA: 0.09, conY: 2, conB: 6, conA: 0.05, blur: 14, rake: 0.68, fill: 0.5 },
    2: { ambY: 16, ambB: 44, ambA: 0.12, conY: 4, conB: 12, conA: 0.07, blur: 20, rake: 0.82, fill: 0.62 },
    3: { ambY: 28, ambB: 70, ambA: 0.15, conY: 7, conB: 20, conA: 0.08, blur: 26, rake: 0.92, fill: 0.7 },
    4: { ambY: 42, ambB: 104, ambA: 0.18, conY: 11, conB: 30, conA: 0.09, blur: 32, rake: 1, fill: 0.78 },
};

// ============================================================================
// GLASS STYLES
// ============================================================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: accent
        ? `radial-gradient(circle at 100% 0%, ${alpha(accent,.075)}, transparent 34%), linear-gradient(145deg, #FFFFFF 0%, ${alpha(accent,.025)} 100%)`
        : 'linear-gradient(145deg, #FFFFFF 0%, #F9FCFD 100%)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 32px rgba(25,57,78,.10)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: getContrastText(color),
    background: `linear-gradient(145deg, ${color}, ${alpha(color, .72)})`,
    border: '1px solid rgba(255,255,255,.68)',
    boxShadow: `0 7px 16px ${alpha(color,.22)}, inset 0 1px 0 rgba(255,255,255,.48)`,
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? getContrastText(color) : getReadableColor(color),
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    // This component intentionally uses coloured gradients on default (text)
    // MUI Buttons. Keep the computed contrast colour authoritative even if a
    // host/global stylesheet supplies a generic text-button colour.
    color: `${getGradientContrastText(color, deep || color)} !important`,
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: `${getGradientContrastText(color, deep || color)} !important` },
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color: `${getReadableColor(color)} !important`,
    background: 'rgba(255,255,255,.82)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

// ============================================================================
// GLOBAL CSS + ASSETS
// ============================================================================

const GLOBAL_CSS = `
    @keyframes tfAuroraA {
        0%, 100% { transform: translate3d(0,0,0) scale(1);           opacity: 0.55; }
        33%      { transform: translate3d(60px,-40px,0) scale(1.18); opacity: 0.85; }
        66%      { transform: translate3d(-40px,30px,0) scale(0.92); opacity: 0.5; }
    }
    @keyframes tfAuroraB {
        0%, 100% { transform: translate3d(0,0,0) scale(1.05);        opacity: 0.5; }
        40%      { transform: translate3d(-70px,45px,0) scale(0.88); opacity: 0.8; }
        75%      { transform: translate3d(35px,-30px,0) scale(1.2);  opacity: 0.62; }
    }
    @keyframes tfAuroraC {
        0%, 100% { transform: translate3d(0,0,0) scale(0.95);        opacity: 0.6; }
        50%      { transform: translate3d(50px,55px,0) scale(1.25);  opacity: 0.85; }
    }
    @keyframes tfRingSpin    { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
    @keyframes tfRingSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes tfNodeRise {
        0%   { transform: translate3d(0,0,0) scale(0.6);        opacity: 0; }
        12%  { opacity: 0.8; }
        88%  { opacity: 0.4; }
        100% { transform: translate3d(26px,-104vh,0) scale(1.1); opacity: 0; }
    }
    @keyframes tfScanSweep {
        0%   { transform: translate3d(0,-14vh,0); opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translate3d(0,114vh,0); opacity: 0; }
    }
    @keyframes tfSignalWave {
        0%   { transform: scale(0.6); opacity: 0.8; }
        70%  { opacity: 0.12; }
        100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes tfBreathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.06); }
    }
    @keyframes tfSheen {
        0%   { transform: translateX(-130%) skewX(-18deg); }
        100% { transform: translateX(320%)  skewX(-18deg); }
    }
    @keyframes tfFloatIcon {
        0%, 100% { transform: translate3d(0,0,0) rotate(0deg);     opacity: 0.09; }
        50%      { transform: translate3d(0,-16px,0) rotate(5deg); opacity: 0.18; }
    }
    @keyframes tfGrain {
        0%   { transform: translate3d(0,0,0); }
        20%  { transform: translate3d(-2%,2%,0); }
        40%  { transform: translate3d(2%,-1%,0); }
        60%  { transform: translate3d(-1%,-2%,0); }
        80%  { transform: translate3d(1%,1%,0); }
        100% { transform: translate3d(0,0,0); }
    }
    @keyframes tfEdgeShift {
        0%   { background-position: 0% 0%; }
        100% { background-position: 300% 0%; }
    }
    @keyframes tfSkeleton {
        0%   { transform: translateX(-120%); }
        100% { transform: translateX(320%); }
    }
    @keyframes tfPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.5; transform: scale(0.8); }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700' +
    '&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

let assetsInjected = false;
const injectAssets = () => {
    if (assetsInjected || typeof document === 'undefined') return;
    assetsInjected = true;
    const link = (rel, href, cross) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = href;
        if (cross) el.crossOrigin = 'anonymous';
        document.head.appendChild(el);
    };
    link('preconnect', 'https://fonts.googleapis.com');
    link('preconnect', 'https://fonts.gstatic.com', true);
    link('stylesheet', FONT_HREF);

    const style = document.createElement('style');
    style.setAttribute('data-tf', 'companies');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
};

// ============================================================================
// FIELD NORMALISATION
// ============================================================================

const asList = (v) => {
    if (v === null || v === undefined || v === '') return [];
    if (Array.isArray(v)) return v.filter(Boolean);
    return [v];
};

const asText = (v, sep = ', ') => asList(v).join(sep);

// ============================================================================
// AI MATCHING FUNCTIONS
// ============================================================================

const calculateCompanyScore = (company, userCompany) => {
    let score = 0;
    let matchDetails = [];

    if (company.primaryService && userCompany?.primaryService) {
        if (company.primaryService === userCompany.primaryService) {
            score += 30;
            matchDetails.push(`Same primary service: ${company.primaryService}`);
        } else if (company.primaryService === userCompany.secondaryService) {
            score += 25;
            matchDetails.push(`Your secondary service matches their primary: ${company.primaryService}`);
        } else if (company.secondaryService === userCompany.primaryService) {
            score += 25;
            matchDetails.push(`Your primary service matches their secondary: ${company.secondaryService}`);
        }
    }

    const regionHits = asList(company.primaryRegion).filter(r => asList(userCompany?.primaryRegion).includes(r));
    if (regionHits.length > 0) {
        score += 20;
        matchDetails.push(`Same primary region: ${regionHits.join(', ')}`);
    }

    const connectHits = asList(company.primaryConnectWith).filter(c => asList(userCompany?.primaryConnectWith).includes(c));
    if (connectHits.length > 0) {
        score += 20;
        matchDetails.push(`Same connect with interest: ${connectHits.join(', ')}`);
    }

    if (company.companyType && userCompany?.companyType) {
        if (company.companyType === userCompany.companyType) {
            score += 15;
            matchDetails.push(`Same company type: ${company.companyType}`);
        }
    }

    if (company.isVerified) score += 5;
    if (company.isActive && !company.isBlocked) score += 5;

    return { score: Math.min(score, 100), details: matchDetails };
};

const generatePersonalizedAIMessage = (company, userCompany, score, matchDetails) => {
    const companyName = company.companyName || 'your company';
    const userCompanyName = userCompany?.companyName || 'our company';

    let matchSummary = matchDetails.slice(0, 4).map(d => `• ${d}`).join('\n');

    let matchLevel = 'moderate';
    let matchEmoji = '📊';
    let openingLine = '';
    let callToAction = '';

    if (score >= 80) {
        matchLevel = 'exceptional';
        matchEmoji = '🎯';
        openingLine = `Our AI-powered analysis has identified an **exceptional ${Math.round(score)}% compatibility** between ${userCompanyName} and ${companyName}.`;
        callToAction = `We would be delighted to schedule a strategic discussion at your earliest convenience.`;
    } else if (score >= 60) {
        matchLevel = 'strong';
        matchEmoji = '🤝';
        openingLine = `Our AI matching system has found **strong synergies** (${Math.round(score)}% match) between ${userCompanyName} and ${companyName}.`;
        callToAction = `Let's schedule a call to discuss possibilities.`;
    } else if (score >= 40) {
        matchLevel = 'moderate';
        matchEmoji = '📊';
        openingLine = `Our analysis shows promising alignment (${Math.round(score)}%) between ${userCompanyName} and ${companyName}.`;
        callToAction = `We'd appreciate the chance to learn more about your current initiatives.`;
    } else {
        matchLevel = 'exploratory';
        matchEmoji = '📫';
        openingLine = `We're reaching out to introduce ${userCompanyName} and explore potential connections.`;
        callToAction = `We're interested in learning more about ${companyName}.`;
    }

    return {
        subject: `${matchEmoji} ${matchLevel.charAt(0).toUpperCase() + matchLevel.slice(1)} Partnership Opportunity: ${userCompanyName} ↔ ${companyName}`,
        message: `Dear ${companyName} Team,

${openingLine}

**AI Match Summary (${Math.round(score)}%):**
${matchSummary || '• Strong synergy in services and regions'}

**About ${userCompanyName}:**
• Primary Service: ${userCompany?.primaryService || 'N/A'}
• Primary Region: ${asText(userCompany?.primaryRegion) || 'Global'}
• Connect With: ${asText(userCompany?.primaryConnectWith) || 'Telecom partners'}

${callToAction}

We look forward to connecting!

Warm regards,
The ${userCompanyName} Team`
    };
};

const calculateUserScore = (user, currentUser) => {
    let score = 0;
    let matchDetails = [];

    const userSpecs = asList(user.specialization);
    const currentSpecs = asList(currentUser?.specialization);
    const specHits = userSpecs.filter(s => currentSpecs.includes(s));
    if (specHits.length > 0) {
        score += 30;
        matchDetails.push(`Shared specialization: ${specHits.join(', ')}`);
    }

    if (user.primaryServiceProvided && currentUser?.primaryServiceProvided) {
        const userServices = asList(user.primaryServiceProvided);
        const currentServices = asList(currentUser.primaryServiceProvided);
        const serviceHits = userServices.filter(s => currentServices.includes(s));
        if (serviceHits.length > 0) {
            score += 25;
            matchDetails.push(`Shared service: ${serviceHits.join(', ')}`);
        }
    }

    if (user.primaryRegionAvailability && currentUser?.primaryRegionAvailability) {
        const userRegions = asList(user.primaryRegionAvailability);
        const currentRegions = asList(currentUser.primaryRegionAvailability);
        const regionHits = userRegions.filter(r => currentRegions.includes(r));
        if (regionHits.length > 0) {
            score += 20;
            matchDetails.push(`Shared region: ${regionHits.join(', ')}`);
        }
    }

    if (user.isVerified) score += 10;
    if (user.isActive && !user.isBlocked) score += 5;

    return { score: Math.min(score, 100), details: matchDetails };
};

const generatePersonalizedIntroMessage = (user, currentUser, score, matchDetails) => {
    const userName = user.displayName || user.firstName || 'there';
    const currentUserName = currentUser?.displayName || currentUser?.firstName || 'I';

    let matchSummary = matchDetails.slice(0, 4).map(d => `• ${d}`).join('\n');

    let introLevel = 'moderate';
    let introEmoji = '📊';
    let openingLine = '';

    if (score >= 80) {
        introLevel = 'exceptional';
        introEmoji = '🎯';
        openingLine = `I noticed we have an **exceptional ${Math.round(score)}% compatibility** based on our shared interests and expertise.`;
    } else if (score >= 60) {
        introLevel = 'strong';
        introEmoji = '🤝';
        openingLine = `Our profiles show **strong alignment** (${Math.round(score)}% match) in the telecom space.`;
    } else if (score >= 40) {
        introLevel = 'moderate';
        introEmoji = '📊';
        openingLine = `I see promising alignment (${Math.round(score)}%) between our professional interests.`;
    } else {
        introLevel = 'exploratory';
        introEmoji = '📫';
        openingLine = `I'm reaching out to explore potential connections in the telecom industry.`;
    }

    return {
        subject: `${introEmoji} ${introLevel.charAt(0).toUpperCase() + introLevel.slice(1)} Connection Opportunity`,
        message: `Hello ${userName},

${openingLine}

**AI Match Summary (${Math.round(score)}%):**
${matchSummary || '• Strong synergy in services and regions'}

**My Profile:**
• Role: ${currentUser?.role || 'Professional'}
• Specialization: ${asText(currentUser?.specialization) || 'N/A'}
• Service: ${asText(currentUser?.primaryServiceProvided) || 'N/A'}
• Region: ${asText(currentUser?.primaryRegionAvailability) || 'Global'}

I'd love to connect and explore how we might collaborate.

Looking forward to hearing from you,

${currentUserName}`
    };
};

// ============================================================================
// AURORA BACKDROP
// ============================================================================

const AuroraBackdrop = memo(() => {
    return (
        <Box
            aria-hidden="true"
            sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0, bgcolor: palette.canvas }}
        >
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    radial-gradient(820px circle at 4% -8%,   ${alpha(palette.rust, 0.14)}, transparent 60%),
                    radial-gradient(680px circle at 96% 10%,  ${alpha(palette.pine, 0.11)}, transparent 58%),
                    radial-gradient(720px circle at 50% 110%, ${alpha(palette.gold, 0.1)}, transparent 60%),
                    radial-gradient(560px circle at 78% 64%,  ${alpha(palette.purple, 0.08)}, transparent 58%)
                `,
            }} />
        </Box>
    );
});
AuroraBackdrop.displayName = 'AuroraBackdrop';

// ============================================================================
// SHARED BITS
// ============================================================================

const SectionRule = memo(({ color = palette.rust, width = 44 }) => (
    <Box sx={{
        width,
        height: 4,
        borderRadius: 999,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0)})`,
        boxShadow: `0 2px 8px ${alpha(color, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.7)`,
        mt: 0.85,
    }} />
));
SectionRule.displayName = 'SectionRule';

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 26, scale: 0.985 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: '100%', width: '100%' }}
    >
        {children}
    </motion.div>
);

const STATUS_CONFIG = {
    active: { label: 'Active', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
    blocked: { label: 'Blocked', color: palette.clay, icon: <BlockIcon sx={{ fontSize: 12 }} /> },
    pending: { label: 'Pending', color: palette.gold, icon: <PendingIcon sx={{ fontSize: 12 }} /> },
    verified: { label: 'Verified', color: palette.pine, icon: <VerifiedIcon sx={{ fontSize: 12 }} /> },
    accepted: { label: 'Connected', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
    connected: { label: 'Connected', color: palette.pine, icon: <HandshakeIcon sx={{ fontSize: 12 }} /> },
    declined: { label: 'Declined', color: palette.clay, icon: <CancelIcon sx={{ fontSize: 12 }} /> },
    sent: { label: 'Sent', color: palette.teal, icon: <SendIcon sx={{ fontSize: 12 }} /> },
    scheduled: { label: 'Scheduled', color: palette.gold, icon: <ScheduleIcon sx={{ fontSize: 12 }} /> },
    completed: { label: 'Completed', color: palette.pine, icon: <CheckIcon sx={{ fontSize: 12 }} /> },
};

const PremiumStatusChip = memo(({ status }) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
        <Box sx={{ ...glassPill(config.color, true), px: 1.2, height: 23, fontSize: '0.62rem' }}>
            {config.icon}
            {config.label}
        </Box>
    );
});
PremiumStatusChip.displayName = 'PremiumStatusChip';

const GlassSkeleton = memo(({ h = 16, w = '100%', radius = '8px', mb = 0 }) => (
    <Box sx={{ ...glassWell(radius), height: h, width: w, mb, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
            position: 'absolute',
            inset: 0,
            width: '38%',
            background: `linear-gradient(90deg, transparent, ${alpha(palette.paper, 0.9)}, transparent)`,
            animation: 'tfSkeleton 1.5s ease-in-out infinite',
            willChange: 'transform',
        }} />
    </Box>
));
GlassSkeleton.displayName = 'GlassSkeleton';

const CardSkeleton = memo(() => (
    <Box sx={{ ...glassCard({ level: 1, radius: '14px' }), p: 3, minHeight: 300 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <GlassSkeleton h={56} w={56} radius="14px" />
            <Box sx={{ flex: 1 }}>
                <GlassSkeleton h={14} w="65%" mb={1} />
                <GlassSkeleton h={11} w="45%" mb={1} />
                <GlassSkeleton h={18} w="80%" radius="999px" />
            </Box>
        </Box>
        <GlassSkeleton h={5} w="100%" radius="999px" mb={2} />
        <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
            <GlassSkeleton h={24} w={70} radius="999px" />
            <GlassSkeleton h={24} w={62} radius="999px" />
            <GlassSkeleton h={24} w={80} radius="999px" />
        </Box>
        <GlassSkeleton h={12} w="100%" mb={0.75} />
        <GlassSkeleton h={12} w="82%" mb={2.5} />
        <Box sx={{ display: 'flex', gap: 1 }}>
            <GlassSkeleton h={34} w="100%" radius="12px" />
            <GlassSkeleton h={34} w={38} radius="12px" />
            <GlassSkeleton h={34} w={38} radius="12px" />
        </Box>
    </Box>
));
CardSkeleton.displayName = 'CardSkeleton';

// ============================================================================
// MEETING CARD SYSTEM
// ============================================================================

const MEETING_ACCENT = {
    scheduled: palette.gold,
    pending: palette.gold,
    accepted: palette.pine,
    confirmed: palette.pine,
    completed: palette.teal,
    declined: palette.clay,
    cancelled: palette.clay,
};

const meetingAccent = (status) => MEETING_ACCENT[String(status || '').toLowerCase()] || palette.rust;

// Meetings arrive from three writers (legacy form, workflow service, imports),
// so every plausible timestamp shape is normalised to a real Date here.
const resolveMeetingDate = (meeting) => {
    const candidates = [
        meeting?.scheduledTime,
        meeting?.startTime,
        meeting?.dateTime,
        meeting?.meetingDate,
        meeting?.date,
    ];
    for (const value of candidates) {
        if (!value) continue;
        if (typeof value?.toDate === 'function') {
            const d = value.toDate();
            if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
        }
        if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
        if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
        if (typeof value === 'number' && Number.isFinite(value)) return new Date(value);
        if (typeof value === 'string' && value.trim()) {
            const raw = value.trim();
            const withTime = meeting?.time && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? `${raw}T${meeting.time}` : raw;
            const parsed = new Date(withTime);
            if (!Number.isNaN(parsed.getTime())) return parsed;
        }
    }
    return null;
};

const formatMeetingParts = (meeting) => {
    const date = resolveMeetingDate(meeting);
    if (!date) {
        return {
            month: 'TBD', day: '–', weekday: '',
            dateLabel: 'Date to be confirmed',
            timeLabel: meeting?.time || 'Time to be confirmed',
            relative: '', isToday: false, isPast: false,
        };
    }
    const now = new Date();
    const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = Math.round((startOfDay(date) - startOfDay(now)) / 86400000);

    let relative = '';
    if (dayDiff === 0) relative = 'Today';
    else if (dayDiff === 1) relative = 'Tomorrow';
    else if (dayDiff > 1 && dayDiff <= 30) relative = `In ${dayDiff} days`;
    else if (dayDiff === -1) relative = 'Yesterday';
    else if (dayDiff < -1) relative = `${Math.abs(dayDiff)} days ago`;

    return {
        month: date.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
        day: String(date.getDate()).padStart(2, '0'),
        weekday: date.toLocaleString('en-US', { weekday: 'short' }),
        dateLabel: date.toLocaleString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        timeLabel: date.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit' }),
        relative,
        isToday: dayDiff === 0,
        isPast: date.getTime() < now.getTime(),
    };
};

const meetingCounterpart = (meeting) =>
    meeting?.targetName || meeting?.targetCompanyName || meeting?.requesterName ||
    meeting?.companyName || meeting?.participantName || 'TELCOFAIR member';

const MeetingDatePlaque = memo(({ accent, parts, muted = false }) => (
    <Box
        className="meeting-plaque"
        sx={{
            width: 62,
            flexShrink: 0,
            borderRadius: '15px',
            overflow: 'hidden',
            textAlign: 'center',
            color: getContrastText(accent),
            background: muted
                ? `linear-gradient(160deg, ${alpha(accent, .55)}, ${alpha(accent, .38)})`
                : `linear-gradient(160deg, ${accent}, ${alpha(accent, .70)})`,
            border: '1px solid rgba(255,255,255,.55)',
            boxShadow: `0 9px 20px ${alpha(accent, .26)}, inset 0 1px 0 rgba(255,255,255,.5)`,
            transition: `transform .3s ${BOUNCE}`,
        }}
    >
        <Typography sx={{ fontSize: '.5rem', fontWeight: 900, letterSpacing: '.11em', py: .38, bgcolor: 'rgba(0,0,0,.16)' }}>
            {parts.month}
        </Typography>
        <Typography sx={{ fontSize: '1.42rem', fontWeight: 880, lineHeight: 1.06, mt: .35, letterSpacing: '-.04em' }}>
            {parts.day}
        </Typography>
        <Typography sx={{ fontSize: '.47rem', fontWeight: 750, opacity: .88, pb: .55, textTransform: 'uppercase', letterSpacing: '.07em' }}>
            {parts.weekday}
        </Typography>
    </Box>
));
MeetingDatePlaque.displayName = 'MeetingDatePlaque';

const MeetingCard = memo(({ meeting, onAction, onJoin, processing = false }) => {
    const accent = meetingAccent(meeting.status);
    const parts = formatMeetingParts(meeting);
    const isVirtual = !meeting.type || meeting.type === 'virtual' || meeting.type === 'video';
    const isActionable = ['scheduled', 'pending'].includes(String(meeting.status || '').toLowerCase());
    const isLive = ['accepted', 'confirmed'].includes(String(meeting.status || '').toLowerCase());
    const counterpart = meetingCounterpart(meeting);
    const duration = meeting.duration ? `${meeting.duration} min` : '30 min';

    return (
        <Reveal delay={0.03}>
            <Paper
                elevation={0}
                sx={{
                    ...glassCard({ level: 2, radius: '18px', accent }),
                    p: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: `transform .24s ${EASE}, box-shadow .24s ease, border-color .24s ease`,
                    '&:hover': {
                        transform: 'translateY(-3px)',
                        ...glassLift(3, accent),
                        '& .meeting-plaque': { transform: 'scale(1.05) rotate(-3deg)' },
                        '& .meeting-sheen': { animation: `tfSheen .95s ${EASE}` },
                    },
                }}
            >
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accent}, ${alpha(accent, .45)}, transparent 88%)`, zIndex: 3 }} />
                <Box className="meeting-sheen" sx={{
                    position: 'absolute', top: 0, bottom: 0, left: 0, width: '36%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,.42), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)', pointerEvents: 'none', zIndex: 2,
                }} />
                <Box sx={{ position: 'absolute', width: 138, height: 138, right: -50, top: -56, borderRadius: '50%', bgcolor: alpha(accent, .07), pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', width: 96, height: 96, right: 26, bottom: -64, borderRadius: '38% 62% 60% 40%', bgcolor: alpha(accent, .05), transform: 'rotate(24deg)', pointerEvents: 'none' }} />
                <CalendarMonthIcon sx={{ position: 'absolute', right: 12, bottom: 4, fontSize: 78, color: alpha(accent, .05), transform: 'rotate(-9deg)', pointerEvents: 'none' }} />

                <Box sx={{ position: 'relative', zIndex: 4, p: 1.6, display: 'flex', flexDirection: 'column', flex: 1, gap: 1.1 }}>
                    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', minWidth: 0 }}>
                        <MeetingDatePlaque accent={accent} parts={parts} />

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: .7, minWidth: 0 }}>
                                <Typography sx={{ flex: 1, minWidth: 0, fontSize: '.86rem', fontWeight: 850, color: palette.ink, lineHeight: 1.22, letterSpacing: '-.015em', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    {meeting.title || 'Business meeting'}
                                </Typography>
                                <PremiumStatusChip status={meeting.status} />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .45, mt: .45, color: palette.inkLight, minWidth: 0 }}>
                                <PersonIcon sx={{ fontSize: 12, color: getReadableColor(accent), flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '.57rem', fontWeight: 650 }} noWrap>{counterpart}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: .45, mt: .65, flexWrap: 'wrap' }}>
                                <Box sx={{ ...glassPill(accent), px: .8, minHeight: 20, fontSize: '.53rem' }}>
                                    <AccessTimeIcon sx={{ fontSize: 10 }} />{parts.timeLabel} · {duration}
                                </Box>
                                <Box sx={{ ...glassPill(isVirtual ? palette.teal : palette.orange), px: .8, minHeight: 20, fontSize: '.53rem', maxWidth: '100%' }}>
                                    {isVirtual ? <VideoCallIcon sx={{ fontSize: 10 }} /> : <LocationOnIcon2 sx={{ fontSize: 10 }} />}
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {isVirtual ? 'Virtual' : meeting.location || 'In-person'}
                                    </Box>
                                </Box>
                                {parts.relative && (
                                    <Box sx={{ ...glassPill(parts.isToday ? palette.success : palette.purple, parts.isToday), px: .8, minHeight: 20, fontSize: '.53rem' }}>
                                        {parts.isToday
                                            ? <LiveIcon sx={{ fontSize: 8 }} />
                                            : <TodayIcon sx={{ fontSize: 10 }} />}
                                        {parts.relative}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {meeting.agenda && (
                        <Box sx={{ ...glassWell('11px', accent), p: 1 }}>
                            <Typography sx={{ fontSize: '.47rem', fontWeight: 900, letterSpacing: '.08em', textTransform: 'uppercase', color: getReadableColor(accent), display: 'flex', alignItems: 'center', gap: .4 }}>
                                <NotesIcon sx={{ fontSize: 10 }} /> Agenda
                            </Typography>
                            <Typography sx={{ mt: .32, fontSize: '.63rem', lineHeight: 1.5, color: palette.inkSoft, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {meeting.agenda}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ mt: 'auto', pt: .95, borderTop: `1px solid ${alpha(palette.ink, .06)}`, display: 'flex', alignItems: 'center', gap: .55, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '.5rem', color: palette.inkFaint, fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
                            {parts.dateLabel}
                        </Typography>

                        {isActionable && (
                            <>
                                <Button
                                    size="small"
                                    disabled={processing}
                                    startIcon={<ThumbUpIcon sx={{ fontSize: 13 }} />}
                                    onClick={() => onAction(meeting.id, 'accept')}
                                    sx={{ ...glassButton(palette.pine, palette.pineDeep), minHeight: 29, fontSize: '.57rem' }}
                                >
                                    Accept
                                </Button>
                                <Button
                                    size="small"
                                    disabled={processing}
                                    startIcon={<ThumbDownIcon sx={{ fontSize: 13 }} />}
                                    onClick={() => onAction(meeting.id, 'decline')}
                                    sx={{ ...glassGhostButton(palette.clay), minHeight: 29, fontSize: '.57rem' }}
                                >
                                    Decline
                                </Button>
                            </>
                        )}

                        {isLive && (
                            <Button
                                size="small"
                                startIcon={<VideoCallIcon sx={{ fontSize: 13 }} />}
                                onClick={() => onJoin(meeting)}
                                sx={{ ...glassButton(palette.gold, palette.goldDeep), minHeight: 29, fontSize: '.57rem' }}
                            >
                                Join
                            </Button>
                        )}
                    </Box>
                </Box>
            </Paper>
        </Reveal>
    );
});
MeetingCard.displayName = 'MeetingCard';

const PastMeetingRow = memo(({ meeting }) => {
    const accent = meetingAccent(meeting.status);
    const parts = formatMeetingParts(meeting);
    const isDone = String(meeting.status || '').toLowerCase() === 'completed';

    return (
        <Paper
            elevation={0}
            sx={{
                ...glassCard({ level: 0, radius: '15px', accent }),
                p: 1.15,
                display: 'flex',
                alignItems: 'center',
                gap: 1.1,
                opacity: .92,
                filter: 'saturate(.88)',
                transition: `transform .2s ${EASE}, opacity .2s ease`,
                '&:hover': { transform: 'translateY(-2px)', opacity: 1, filter: 'none' },
            }}
        >
            <MeetingDatePlaque accent={accent} parts={parts} muted />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: '.74rem', fontWeight: 800, color: palette.ink, lineHeight: 1.2 }} noWrap>
                    {meeting.title || 'Business meeting'}
                </Typography>
                <Typography sx={{ mt: .2, fontSize: '.53rem', color: palette.inkFaint }} noWrap>
                    {meetingCounterpart(meeting)} · {parts.timeLabel}
                </Typography>
                <Box sx={{ mt: .5, display: 'flex', gap: .4, flexWrap: 'wrap' }}>
                    <PremiumStatusChip status={meeting.status} />
                    {parts.relative && (
                        <Box sx={{ ...glassPill(palette.inkFaint), px: .75, minHeight: 20, fontSize: '.5rem' }}>
                            <HistoryIcon sx={{ fontSize: 10 }} />{parts.relative}
                        </Box>
                    )}
                </Box>
            </Box>
            <Box sx={{ ...glassPod(isDone ? palette.pine : palette.clay, 30, '9px') }}>
                {isDone ? <CheckIcon sx={{ fontSize: 15 }} /> : <CancelIcon sx={{ fontSize: 15 }} />}
            </Box>
        </Paper>
    );
});
PastMeetingRow.displayName = 'PastMeetingRow';

// ============================================================================
// STAT CARD
// ============================================================================

const PremiumStatCard = memo(({ title, value, icon, overlayIcon, color, delay }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);
    const colorInk = getReadableColor(color);
    const iconContrast = getContrastText(color);

    useEffect(() => {
        const node = nodeRef.current;
        if (!node) return undefined;

        if (typeof value !== 'number' || value <= 0) {
            node.textContent = '0';
            return undefined;
        }
        if (hasRun.current) {
            node.textContent = value.toLocaleString();
            return undefined;
        }

        let raf = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || hasRun.current) return;
                hasRun.current = true;
                const start = performance.now();
                const tick = (now) => {
                    const t = Math.min(1, (now - start) / 900);
                    const eased = 1 - Math.pow(1 - t, 3);
                    if (nodeRef.current) nodeRef.current.textContent = Math.round(value * eased).toLocaleString();
                    if (t < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
                observer.disconnect();
            },
            { threshold: 0.3 }
        );
        observer.observe(node);

        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [value]);

    return (
        <Reveal delay={delay}>
            <Card
                elevation={0}
                className="tf-color-card tf-company-directory-card"
                sx={{
                    '--card-color': color,
                    '--card-contrast': iconContrast,
                    height: '100%',
                    minHeight: 124,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    background: `radial-gradient(circle at 92% 12%, ${alpha(color,.12)}, transparent 30%), linear-gradient(145deg,#FFFFFF,${alpha(color,.055)}) !important`,
                    border: `1px solid ${alpha(color,.14)} !important`,
                    boxShadow: `0 12px 30px rgba(25,57,78,.085),0 3px 9px ${alpha(color,.035)} !important`,
                    transition: `transform 0.3s ${EASE}, box-shadow 0.3s ease`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 20px 42px ${alpha(color,.14)},0 6px 14px rgba(25,57,78,.055) !important`,
                        '& .stat-pod': { transform: 'scale(1.06) rotate(-5deg)' },
                        '& .stat-sheen': { animation: `tfSheen 0.9s ${EASE}` },
                    },
                }}
            >
                <Box className="tf-card-dotgrid" sx={{ opacity: .085 }} />
                <Box className="tf-card-microbars">
                    {[7, 13, 19, 11, 24].map((height, idx) => <Box component="span" key={idx} sx={{ height }} />)}
                </Box>
                <Box className="stat-sheen" sx={{
                    position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.46), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)',
                    pointerEvents: 'none', zIndex: 2,
                }} />
                <Box sx={{ position: 'absolute', right: -28, bottom: -48, width: 122, height: 122, borderRadius: '50%', border: `15px solid ${alpha(color,.05)}`, pointerEvents: 'none' }} />

                {overlayIcon && (
                    <Box sx={{ position: 'absolute', right: 7, bottom: -18, opacity: .065, transform: 'rotate(-10deg)', pointerEvents: 'none', '& svg': { fontSize: 88, color: colorInk } }}>
                        {overlayIcon}
                    </Box>
                )}

                <CardContent sx={{ p: '14px !important', flex: 1, display: 'grid', gridTemplateColumns: '46px minmax(0,1fr)', gap: 1.15, alignItems: 'start', position: 'relative', zIndex: 1 }}>
                    <Box className="stat-pod tf-color-icon" sx={{ '--card-color': color, '--card-contrast': iconContrast, width: 46, height: 46, borderRadius: '13px', display: 'grid', placeItems: 'center', transition: `transform .28s ${BOUNCE}` }}>
                        {React.cloneElement(icon, { sx: { fontSize: 21 } })}
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: .7 }}>
                            <Typography sx={{ color: palette.inkSoft, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.065em', fontSize: '0.59rem' }} noWrap>
                                {title}
                            </Typography>
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .35, px: .65, height: 19, borderRadius: 999, color: palette.success, bgcolor: alpha(palette.success,.07), border: `1px solid ${alpha(palette.success,.10)}`, fontSize: '.46rem', fontWeight: 760 }}>
                                <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: palette.success }} /> LIVE
                            </Box>
                        </Box>
                        <Typography sx={{ mt: .25, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 860, fontSize: 'clamp(1.5rem,2.4vw,1.85rem)', lineHeight: 1.02, letterSpacing: '-.045em', color: palette.ink }}>
                            <span ref={nodeRef}>0</span>
                        </Typography>
                        <Box sx={{ mt: .65, display: 'flex', alignItems: 'center', gap: .5 }}>
                            <Box sx={{ width: 18, height: 3, borderRadius: 99, background: `linear-gradient(90deg,${color},${alpha(color,.38)})` }} />
                            <Typography sx={{ color: palette.inkFaint, fontSize: '.5rem', fontWeight: 600 }}>Live directory data</Typography>
                        </Box>
                    </Box>
                </CardContent>
                <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: `linear-gradient(90deg,${color},${alpha(color,.5)},transparent 90%)` }} />
            </Card>
        </Reveal>
    );
});

PremiumStatCard.displayName = 'PremiumStatCard';

// ============================================================================
// PROFILE LABEL COMPONENT
// ============================================================================

const ProfileLabel = ({ icon, label, value, color = palette.teal }) => {
    const ink = getReadableColor(color);
    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: '28px minmax(72px, auto) 1fr',
            alignItems: 'center',
            gap: 0.8,
            px: 0.75,
            py: 0.62,
            borderRadius: '10px',
            background: `linear-gradient(90deg, ${alpha(color, 0.075)}, ${alpha(color, 0.018)})`,
            border: `1px solid ${alpha(color, 0.10)}`,
            transition: 'transform .18s ease, background .18s ease, border-color .18s ease',
            '&:hover': {
                transform: 'translateX(2px)',
                background: `linear-gradient(90deg, ${alpha(color, 0.11)}, ${alpha(color, 0.025)})`,
                borderColor: alpha(color, 0.18),
            },
        }}>
            <Box sx={{
                width: 26,
                height: 26,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '8px',
                color: getContrastText(color),
                background: `linear-gradient(145deg, ${color}, ${alpha(color, 0.76)})`,
                boxShadow: `0 5px 12px ${alpha(color, 0.22)}, inset 0 1px 0 rgba(255,255,255,.45)`,
                '& svg': { fontSize: 14 },
            }}>
                {icon}
            </Box>
            <Typography variant="caption" sx={{
                color: ink,
                fontSize: '0.56rem',
                fontWeight: 800,
                textTransform: 'uppercase',
                letterSpacing: '0.055em',
            }}>
                {label}
            </Typography>
            <Typography variant="body2" title={String(value || 'Not specified')} sx={{
                color: palette.ink,
                fontSize: '0.72rem',
                fontWeight: 650,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'right',
            }}>
                {value || 'Not specified'}
            </Typography>
        </Box>
    );
};

// ============================================================================
// PEOPLE CARD WITH REQUEST STATUS
// ============================================================================

const PeopleCard = memo(({ user, onClick, onIntroduce, onChat, onSchedule, introStatus, isLocked = false, isRequestReceived = false }) => {
    const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
    const initials = displayName.charAt(0).toUpperCase();
    const hasProfileImage = Boolean(user.profileImage);
    const role = user.role || 'Member';
    const companyName = user.companyName || 'N/A';

    const specializations = asList(user.specialization);
    const primaryServices = asList(user.primaryServiceProvided);
    const regions = asList(user.primaryRegionAvailability);

    const aiScore = user.aiScore || 0;
    const scoreColor = aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : palette.rust;
    const scoreInk = getReadableColor(scoreColor);

    const getStatusDisplay = () => {
        if (isRequestReceived && introStatus === 'pending') {
            return { label: 'Request from you', color: palette.gold, icon: <PendingIcon sx={{ fontSize: 12 }} /> };
        }
        if (introStatus === 'pending') {
            return { label: 'Awaiting Response', color: palette.gold, icon: <PendingIcon sx={{ fontSize: 12 }} /> };
        }
        if (introStatus === 'accepted') {
            return { label: 'Connected', color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> };
        }
        if (introStatus === 'rejected') {
            return { label: 'Declined', color: palette.clay, icon: <CancelIcon sx={{ fontSize: 12 }} /> };
        }
        return null;
    };

    const statusDisplay = getStatusDisplay();
    const isPending = introStatus === 'pending';
    const isAccepted = introStatus === 'accepted';
    const isRejected = introStatus === 'rejected';

    return (
        <Reveal delay={0.04}>
            <Card
                elevation={0}
                onClick={onClick}
                sx={{
                    ...glassCard({ level: 1, radius: '14px', accent: isLocked || isPending ? palette.gold : isAccepted ? palette.pine : palette.teal }),
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    overflow: 'hidden',
                    opacity: isRejected ? 0.7 : 1,
                    transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                    '&:hover': {
                        transform: 'translateY(-2px)',
                        ...glassLift(3, isLocked || isPending ? palette.gold : isAccepted ? palette.pine : palette.teal),
                        '& .card-sheen': { animation: `tfSheen 0.95s ${EASE}` },
                        '& .user-avatar': { transform: 'scale(1.05) rotate(-4deg)' },
                    },
                    position: 'relative',
                }}
            >
                <Box className="card-sheen" sx={{
                    position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                    transform: 'translateX(-130%) skewX(-18deg)',
                    pointerEvents: 'none', zIndex: 3,
                }} />

                <Box sx={{
                    height: '3px',
                    background: `linear-gradient(90deg, ${isAccepted ? palette.pine : isPending ? palette.gold : isLocked ? palette.gold : palette.teal}, ${alpha(isAccepted ? palette.pine : isPending ? palette.gold : isLocked ? palette.gold : palette.teal, 0.35)}, transparent)`,
                    flexShrink: 0, position: 'relative', zIndex: 2,
                }} />

                {statusDisplay && (
                    <Box sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        ...glassPill(statusDisplay.color, true),
                        px: 1.2,
                        height: 22,
                        fontSize: '0.55rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 10,
                    }}>
                        {statusDisplay.icon}
                        {statusDisplay.label}
                    </Box>
                )}

                <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Avatar
                            className="user-avatar"
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                background: hasProfileImage ? alpha(palette.paper, 0.7) : `linear-gradient(135deg, ${isAccepted ? palette.pine : isPending ? palette.gold : palette.teal}, ${isAccepted ? palette.pineDeep : isPending ? palette.goldDeep : palette.tealDeep})`,
                                backdropFilter: 'blur(10px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                                border: '1px solid rgba(255,255,255,0.5)',
                                boxShadow: `0 10px 24px ${alpha(isAccepted ? palette.pine : isPending ? palette.gold : palette.teal, 0.28)}, inset 0 1px 0 rgba(255,255,255,0.7)`,
                                flexShrink: 0,
                                color: getGradientContrastText(isAccepted ? palette.pine : isPending ? palette.gold : palette.teal, isAccepted ? palette.pineDeep : isPending ? palette.goldDeep : palette.tealDeep),
                                fontWeight: 700,
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontSize: '1.2rem',
                                transition: `transform 0.45s ${BOUNCE}`,
                            }}
                        >
                            {hasProfileImage ? (
                                <img
                                    src={user.profileImage}
                                    alt={displayName}
                                    loading="lazy"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '16px' }}
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                />
                            ) : initials}
                        </Avatar>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography
                                    variant="h6"
                                    fontWeight={700}
                                    sx={{ fontSize: '1rem', color: palette.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {displayName}
                                </Typography>
                                {user.isVerified && (
                                    <Tooltip title="Verified User">
                                        <VerifiedIcon sx={{ fontSize: 16, color: palette.pine, flexShrink: 0 }} />
                                    </Tooltip>
                                )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.55, mt: 0.45, flexWrap: 'wrap' }}>
                                <Box sx={{ ...glassPill(palette.purple), px: 0.8, minHeight: 20, fontSize: '0.58rem' }}>
                                    <WorkIcon sx={{ fontSize: 11 }} /> {role}
                                </Box>
                                <Box sx={{ ...glassPill(palette.teal), px: 0.8, minHeight: 20, fontSize: '0.58rem', maxWidth: '100%' }}>
                                    <BusinessIcon sx={{ fontSize: 11 }} />
                                    <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companyName}</Box>
                                </Box>
                                {user.country && (
                                    <Box sx={{ ...glassPill(palette.orange), px: 0.8, minHeight: 20, fontSize: '0.58rem' }}>
                                        <LocationIcon sx={{ fontSize: 11 }} /> {user.country}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.6 }}>
                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                AI Match Score
                            </Typography>
                            <Typography variant="caption" sx={{ color: scoreInk, fontSize: '0.78rem', fontWeight: 800 }}>
                                {Math.round(aiScore)}%
                            </Typography>
                        </Box>
                        <Box sx={{ ...glassWell('999px'), p: '2px' }}>
                            <LinearProgress
                                variant="determinate"
                                value={aiScore}
                                sx={{
                                    height: 5,
                                    borderRadius: 999,
                                    bgcolor: 'transparent',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 999,
                                        background: `linear-gradient(90deg, ${alpha(scoreColor, 0.7)}, ${scoreColor})`,
                                        boxShadow: `0 1px 8px ${alpha(scoreColor, 0.55)}`,
                                    },
                                }}
                            />
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                        {[
                            { icon: <BusinessIcon />, label: 'Company', value: user.companyName || 'N/A', color: palette.teal },
                            { icon: <StarsIcon />, label: 'Specialization', value: specializations.slice(0, 2).join(', ') || 'N/A', color: palette.purple },
                            { icon: <DiamondIcon />, label: 'Service', value: primaryServices.slice(0, 2).join(', ') || 'N/A', color: palette.rust },
                            { icon: <PublicIcon />, label: 'Region', value: regions.slice(0, 2).join(', ') || 'N/A', color: palette.orange },
                        ].filter(item => item.value !== 'N/A').map((item, idx) => (
                            <ProfileLabel key={idx} icon={item.icon} label={item.label} value={item.value} color={item.color} />
                        ))}
                    </Box>

                    <Box sx={{ 
                        display: 'flex', 
                        gap: 1, 
                        mt: 'auto',
                        pt: 1.5, 
                        borderTop: `1px solid ${alpha(palette.ink, 0.07)}`,
                        flexWrap: 'wrap',
                    }}>
                        {isRejected ? (
                            <Box sx={{ ...glassPill(palette.clay), flex: 1, justifyContent: 'center', height: 34, fontSize: '0.65rem' }}>
                                <CancelIcon sx={{ fontSize: 14 }} /> Request Declined
                            </Box>
                        ) : isPending ? (
                            <Box sx={{ ...glassPill(palette.gold, true), flex: 1, justifyContent: 'center', height: 34, fontSize: '0.65rem' }}>
                                <PendingIcon sx={{ fontSize: 14 }} /> {isRequestReceived ? 'Awaiting Your Response' : 'Pending...'}
                            </Box>
                        ) : isAccepted ? (
                            <>
                                <Button
                                    size="small"
                                    startIcon={<ChatIcon sx={{ fontSize: 14 }} />}
                                    onClick={(e) => { e.stopPropagation(); onChat(user); }}
                                    sx={{ ...glassButton(palette.gold, palette.goldDeep), flex: 1, fontSize: '0.65rem', py: 0.7 }}
                                >
                                    Chat
                                </Button>
                                <Button
                                    size="small"
                                    startIcon={<EventIcon sx={{ fontSize: 14 }} />}
                                    onClick={(e) => { e.stopPropagation(); onSchedule(user); }}
                                    sx={{ ...glassButton(palette.rust, palette.rustDeep), flex: 1, fontSize: '0.65rem', py: 0.7 }}
                                >
                                    Meet
                                </Button>
                            </>
                        ) : (
                            <Button
                                size="small"
                                startIcon={<AiIcon sx={{ fontSize: 14 }} />}
                                onClick={(e) => { e.stopPropagation(); onIntroduce(user); }}
                                sx={{ ...glassButton(palette.purple, palette.navy), flex: 1, fontSize: '0.65rem', py: 0.7 }}
                            >
                                AI Intro
                            </Button>
                        )}
                        <Button
                            size="small"
                            startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                            onClick={(e) => { e.stopPropagation(); onClick(); }}
                            sx={{ ...glassGhostButton(palette.inkFaint), fontSize: '0.65rem', py: 0.7, minWidth: 'auto' }}
                        >
                            View
                        </Button>
                    </Box>

                    {isPending && (
                        <Box sx={{ ...glassWell('8px'), mt: 1.5, p: 1.2 }}>
                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.55rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isRequestReceived ? (
                                    <><NotificationIcon sx={{ fontSize: 11 }} /> They sent you an introduction — respond in the Invitations tab</>
                                ) : (
                                    <><LockIcon sx={{ fontSize: 11 }} /> Waiting for them to accept your introduction</>
                                )}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Reveal>
    );
});
PeopleCard.displayName = 'PeopleCard';

// ============================================================================
// PREMIUM COMPANY CARD
// ============================================================================

const COVER_TINTS = [palette.blue, palette.green, palette.teal, palette.orange, palette.purple, palette.rose];

const PremiumCompanyCard = memo(({ company, accentColor, accentGradient, onClick, onAIConnect, onChat }) => {
    const initials = (company.companyName || 'C').charAt(0).toUpperCase();
    const logoImage = getCompanyLogoUrl(company);
    const coverImage = getCompanyCoverUrl(company);
    const hasLogo = Boolean(logoImage);
    const aiScore = Number(company.aiScore || 0);
    const scoreColor = aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : accentColor;
    const scoreInk = getReadableColor(scoreColor);
    const accentInk = getReadableColor(accentColor);
    const iconContrast = getContrastText(accentColor);
    const aiActionContrast = getGradientContrastText(accentColor, palette.purple);
    const regions = asList(company.primaryRegion);
    const connects = asList(company.primaryConnectWith);

    const invitationStatus = company.invitationStatus;
    const isConnected = invitationStatus === 'accepted' || invitationStatus === 'connected';
    const isCompanyActive = company.isActive !== false && company.status !== 'inactive';
    const isPending = invitationStatus === 'pending';
    const isSender = company.invitationIsSender;

    return (
        <Reveal delay={0.035}>
            <Card
                elevation={0}
                onClick={onClick}
                className="tf-color-card tf-company-directory-card tf-premium-company-card"
                sx={{
                    '--card-color': accentColor,
                    '--card-contrast': iconContrast,
                    minHeight: 270,
                    height: '100%',
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    position: 'relative',
                    overflow: 'hidden',
                    background: `radial-gradient(circle at 94% 10%, ${alpha(accentColor,.12)}, transparent 30%), linear-gradient(145deg, rgba(255,255,255,.995), ${alpha(accentColor,.06)}) !important`,
                    border: `1px solid ${alpha(accentColor,.13)} !important`,
                    boxShadow: `0 12px 30px rgba(25,57,78,.085), 0 3px 9px ${alpha(accentColor,.04)} !important`,
                    transition: `transform .22s ${EASE}, box-shadow .22s ease, border-color .22s ease`,
                    ...(isPending && { opacity: .84, filter: 'saturate(.82)' }),
                    '&:hover': {
                        transform: 'translateY(-3px)',
                        borderColor: `${alpha(accentColor,.24)} !important`,
                        boxShadow: `0 20px 42px ${alpha(accentColor,.15)}, 0 6px 14px rgba(25,57,78,.06) !important`,
                        '& .company-card-icon': { transform: 'scale(1.08) rotate(-4deg)' },
                    },
                }}
            >
                {/* contextual decorative graphics — intentionally behind content */}
                <Box className="tf-card-dotgrid" sx={{ right: 11, top: 11, opacity: .08 }} />
                <Box sx={{ position: 'absolute', width: 145, height: 145, right: -52, top: -58, borderRadius: '50%', bgcolor: alpha(accentColor,.08), pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', width: 110, height: 110, right: 28, bottom: -72, borderRadius: '36% 64% 62% 38%', bgcolor: alpha(accentColor,.055), transform: 'rotate(26deg)', pointerEvents: 'none' }} />
                <BusinessIcon sx={{ position: 'absolute', right: 14, bottom: 5, fontSize: 72, color: alpha(accentColor,.055), transform: 'rotate(-8deg)', pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor,.45)}, transparent 88%)` }} />

                {isPending && (
                    <Box sx={{ position: 'absolute', right: 10, top: 9, zIndex: 4, display: 'inline-flex', alignItems: 'center', gap: .4, px: .75, height: 20, borderRadius: '999px', color: palette.warning, bgcolor: alpha(palette.warning,.09), border: `1px solid ${alpha(palette.warning,.14)}`, fontSize: '.5rem', fontWeight: 750 }}>
                        <LockIcon sx={{ fontSize: 10 }} /> {isSender ? 'Pending' : 'Requested you'}
                    </Box>
                )}

                <Box
                    className="tf-fixed-cover tf-directory-cover"
                    sx={{
                        height: 92,
                        position: 'relative',
                        zIndex: 1,
                        background: coverImage
                            ? `linear-gradient(180deg, ${alpha(accentColor,.04)}, ${alpha(accentColor,.12)}), url("${coverImage}") center/cover no-repeat`
                            : `linear-gradient(118deg, ${alpha(accentColor,.92)}, ${alpha(palette.teal,.72)})`,
                        borderBottom: `1px solid ${alpha(accentColor,.12)}`,
                        overflow: 'hidden',
                    }}
                >
                    {!coverImage && <BusinessIcon sx={{ position:'absolute', right:10, bottom:-15, fontSize:72, color:'rgba(255,255,255,.13)', transform:'rotate(-10deg)' }} />}
                    <Box sx={{ position:'absolute', inset:0, background:'linear-gradient(90deg,rgba(255,255,255,.04),rgba(255,255,255,.22),rgba(255,255,255,.03))', opacity:.55 }} />
                </Box>

                <CardContent sx={{ p: '0 13px 13px !important', position: 'relative', zIndex: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0, pr: isPending ? 6.2 : 0, mt: -3.2 }}>
                        <Avatar
                            className="company-card-icon"
                            sx={{
                                width: 58, height: 58, borderRadius: '15px', flexShrink: 0,
                                color: hasLogo ? palette.ink : iconContrast, fontSize: '1rem', fontWeight: 800,
                                background: hasLogo ? '#fff' : accentGradient,
                                border: '1px solid rgba(255,255,255,.75)',
                                boxShadow: `0 8px 18px ${alpha(accentColor,.24)}, inset 0 1px 0 rgba(255,255,255,.62)`,
                                transition: `transform .3s ${BOUNCE}`,
                                overflow: 'hidden',
                            }}
                        >
                            {hasLogo ? <img src={logoImage} alt={company.companyName || 'Company'} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} /> : initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .4, minWidth: 0 }}>
                                <Typography sx={{ fontSize: '.78rem', fontWeight: 820, color: palette.ink, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {company.companyName || 'Company'}
                                </Typography>
                                {company.isVerified && <VerifiedIcon sx={{ color: accentInk, fontSize: 14, flexShrink: 0 }} />}
                            </Box>
                            <Typography sx={{ mt: .25, fontSize: '.52rem', color: palette.inkFaint, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {company.primaryService || company.companyType || 'Business Network Member'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: .45, mt: .55, flexWrap: 'wrap' }}>
                                <Box sx={{ px: .7, height: 19, borderRadius: '999px', display: 'inline-flex', alignItems: 'center', color: accentInk, bgcolor: alpha(accentColor,.075), border: `1px solid ${alpha(accentColor,.10)}`, fontSize: '.48rem', fontWeight: 750 }}>{company.companyType || 'Company'}</Box>
                                <Box sx={{ px: .7, height: 19, borderRadius: '999px', display: 'inline-flex', alignItems: 'center', gap: .35, color: isCompanyActive ? palette.success : palette.inkFaint, bgcolor: alpha(isCompanyActive ? palette.success : palette.inkFaint,.065), border: `1px solid ${alpha(isCompanyActive ? palette.success : palette.inkFaint,.10)}`, fontSize: '.48rem', fontWeight: 750 }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: isCompanyActive ? palette.success : palette.inkFaint }} /> {isCompanyActive ? 'Active' : 'Inactive'}
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 1, p: .8, borderRadius: '10px', bgcolor: alpha(accentColor,.04), border: `1px solid ${alpha(accentColor,.075)}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: .8 }}>
                            <Typography sx={{ fontSize: '.5rem', fontWeight: 760, color: palette.inkLight, textTransform: 'uppercase', letterSpacing: '.055em' }}>AI Match</Typography>
                            <Typography sx={{ fontSize: '.7rem', fontWeight: 850, color: scoreInk }}>{Math.round(aiScore)}%</Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, aiScore))} sx={{ mt: .45, height: 4, borderRadius: 99, bgcolor: alpha(scoreColor,.10), '& .MuiLinearProgress-bar': { borderRadius: 99, background: `linear-gradient(90deg,${alpha(scoreColor,.72)},${scoreColor})` } }} />
                    </Box>

                    <Box sx={{ mt: .8, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: .55 }}>
                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: .45, color: palette.inkLight }}>
                            <PublicIcon sx={{ fontSize: 12, color: accentInk, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '.5rem' }} noWrap>{regions.slice(0,2).join(', ') || company.country || 'Global'}</Typography>
                        </Box>
                        <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: .45, color: palette.inkLight }}>
                            <HandshakeIcon sx={{ fontSize: 12, color: accentInk, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '.5rem' }} noWrap>{connects.slice(0,2).join(', ') || 'Partnerships'}</Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mt: 'auto', pt: .9, display: 'flex', alignItems: 'center', gap: .55 }}>
                        {isConnected ? (
                            <Button size="small" onClick={(e) => { e.stopPropagation(); onChat(company); }} startIcon={<ChatIcon sx={{ fontSize: 13 }} />} sx={{ minHeight: 28, flex: 1, color: '#fff !important', background: `linear-gradient(135deg,${palette.success},${palette.teal}) !important`, fontSize: '.55rem !important' }}>Chat</Button>
                        ) : isPending ? (
                            <Button size="small" disabled startIcon={<LockIcon sx={{ fontSize: 12 }} />} sx={{ minHeight: 28, flex: 1, fontSize: '.54rem !important' }}>{isSender ? 'Awaiting acceptance' : 'Request received'}</Button>
                        ) : (
                            <Button size="small" onClick={(e) => { e.stopPropagation(); onAIConnect(company); }} startIcon={<AiIcon sx={{ fontSize: 13 }} />} sx={{ minHeight: 28, flex: 1, color: `${aiActionContrast} !important`, background: `linear-gradient(135deg,${accentColor},${palette.purple}) !important`, fontSize: '.55rem !important', boxShadow: `0 7px 16px ${alpha(accentColor,.20)} !important` }}>AI Connect</Button>
                        )}
                        <EntityRatingButton compact targetType="company" targetId={company.id || company.companyId} targetName={company.companyName || 'Company'} target={company} color="#E9A220" />
                        <Tooltip title="View company">
                            <IconButton onClick={(e) => { e.stopPropagation(); onClick(); }} sx={{ width: 29, height: 29, borderRadius: '9px', color: accentInk, bgcolor: alpha(accentColor,.08), border: `1px solid ${alpha(accentColor,.12)}`, boxShadow: '0 3px 9px rgba(25,57,78,.04)' }}><VisibilityIcon sx={{ fontSize: 14 }} /></IconButton>
                        </Tooltip>
                    </Box>
                </CardContent>
            </Card>
        </Reveal>
    );
});
PremiumCompanyCard.displayName = 'PremiumCompanyCard';

// ============================================================================
// INVITATION / CONNECTION IDENTITY CARD
// ============================================================================

const InvitationIdentityCard = ({ person, company, title, subtitle, status, statusColor = palette.purple, aiScore = 0, dateLabel = '', message = '', children }) => {
    const personName = title || person?.displayName || [person?.firstName, person?.lastName].filter(Boolean).join(' ') || person?.name || company?.companyName || 'Platform member';
    const personPhoto = person?.profileImage || person?.photoURL || person?.profilePicture || person?.avatarUrl || '';
    const companyLogo = getCompanyLogoUrl(company || {});
    const companyCover = getCompanyCoverUrl(company || {});
    const initials = personName.split(/\s+/).slice(0,2).map((part) => part?.[0] || '').join('').toUpperCase() || 'U';
    const isPerson = Boolean(person || personPhoto);

    return (
        <Paper elevation={0} className="tf-invitation-identity-card" sx={{ '--invite-color': statusColor, position:'relative', overflow:'hidden', borderRadius:'18px', border:`1px solid ${alpha(statusColor,.12)}`, background:'#fff', boxShadow:'0 10px 26px rgba(25,57,78,.07)', transition:'transform .22s ease, box-shadow .22s ease, border-color .22s ease', '&:hover':{ transform:'translateY(-2px)', borderColor:alpha(statusColor,.22), boxShadow:`0 17px 34px ${alpha(statusColor,.12)}` } }}>
            <Box className="tf-fixed-cover tf-invitation-cover" sx={{ height:72, position:'relative', overflow:'hidden', background:companyCover ? `linear-gradient(180deg,rgba(7,12,18,.06),rgba(7,12,18,.46)), url("${companyCover}") center/cover no-repeat` : `linear-gradient(125deg,${alpha(statusColor,.92)},${alpha(palette.teal,.75)})` }}>
                <Box aria-hidden="true" className="tf-invite-shine" sx={{ position:'absolute', inset:'0 auto 0 -40%', width:'30%', background:'linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent)', transform:'skewX(-18deg)' }} />
                <Box sx={{ position:'absolute', top:9, right:9, display:'flex', gap:.5 }}>
                    {status && <PremiumStatusChip status={status} />}
                    {aiScore > 0 && <Box sx={{ ...glassPill(palette.purple, true), height:21, px:.9, fontSize:'.5rem' }}><AiIcon sx={{fontSize:10}}/>AI {Math.round(aiScore)}%</Box>}
                </Box>
            </Box>
            <Box sx={{ px:1.4, pb:1.35, position:'relative' }}>
                <Box sx={{ display:'flex', alignItems:'flex-start', gap:1, mt:-2.9, minWidth:0 }}>
                    <Box sx={{ position:'relative', width:60, height:64, flexShrink:0, display:'flex', justifyContent:'center' }}>
                        <Avatar src={(isPerson ? personPhoto : companyLogo) || undefined} sx={{ width:56,height:56,borderRadius:isPerson?'50%':'14px',bgcolor:companyLogo && !isPerson ? '#fff' : statusColor,color:companyLogo && !isPerson ? palette.ink : getContrastText(statusColor),fontSize:'.95rem',fontWeight:850,border:'3px solid #fff',boxShadow:`0 8px 20px ${alpha(statusColor,.23)}`,'& img':{objectFit:isPerson?'cover':'contain',padding:isPerson?0:'4px'} }}>{!(isPerson ? personPhoto : companyLogo) && initials}</Avatar>
                        {isPerson && companyLogo && <Avatar src={companyLogo} sx={{ position:'absolute',left:'50%',transform:'translateX(-50%)',bottom:0,width:23,height:23,borderRadius:'50%',bgcolor:'#fff',border:'2px solid #fff',boxShadow:'0 4px 10px rgba(25,57,78,.18)','& img':{objectFit:'contain',padding:'2px'} }} />}
                    </Box>
                    <Box sx={{ minWidth:0,flex:1,pt:3.2 }}>
                        <Typography sx={{ fontSize:'.8rem',fontWeight:850,color:palette.ink,lineHeight:1.15 }} noWrap>{personName}</Typography>
                        <Typography sx={{ mt:.28,fontSize:'.56rem',fontWeight:650,color:palette.inkLight }} noWrap>{subtitle || person?.jobTitle || person?.designation || company?.companyName || 'TELCOFAIR member'}</Typography>
                        {dateLabel && <Typography sx={{ mt:.22,fontSize:'.49rem',color:palette.inkFaint }}>{dateLabel}</Typography>}
                    </Box>
                </Box>
                {message && <Box sx={{ mt:.9,p:.85,borderRadius:'10px',background:alpha(statusColor,.04),border:`1px solid ${alpha(statusColor,.075)}` }}><Typography sx={{ fontSize:'.57rem',lineHeight:1.5,color:palette.inkSoft,display:'-webkit-box',WebkitLineClamp:3,WebkitBoxOrient:'vertical',overflow:'hidden',whiteSpace:'pre-wrap' }}>{message}</Typography></Box>}
                {children && <Box sx={{ mt:1,pt:.9,borderTop:`1px solid ${palette.hairlineSoft}`,display:'flex',gap:.6,flexWrap:'wrap',alignItems:'center' }}>{children}</Box>}
            </Box>
        </Paper>
    );
};

// ============================================================================
// MAIN COMPONENT - CompaniesListing
// ============================================================================

const COMPANY_TYPES = [
    'Operator', 'Aggregator', 'Platform Provider', 'Infrastructure Provider',
    'Training Provider', 'HR Services', 'Marketing Agency', 'Consultant',
    'Reseller', 'Distributor',
];

const CompaniesListing = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, companyData, currentUser } = useAuth();

    injectAssets();

    // Tab state. Query-state support lets sidebar/deep links open the exact workspace.
    const [activeTab, setActiveTab] = useState(0);
    useEffect(() => {
        const requested = new URLSearchParams(location.search).get('tab') || 'companies';
        const nextTab = ({ companies: 0, invitations: 1, meetings: 2, people: 3 })[requested];
        if (Number.isInteger(nextTab)) setActiveTab(nextTab);
    }, [location.search]);

    const changeWorkspaceTab = useCallback((nextTab) => {
        setActiveTab(nextTab);
        const key = ['companies', 'invitations', 'meetings', 'people'][nextTab] || 'companies';
        navigate(`/dashboard/companies?tab=${key}`, { replace: true });
    }, [navigate]);

    // Company listing state
    const [loading, setLoading] = useState(true);
    const [companies, setCompanies] = useState([]);
    const [filteredCompanies, setFilteredCompanies] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ type: '', status: '', region: '' });
    const [invitations, setInvitations] = useState({});
    const [aiScores, setAiScores] = useState({});
    const [aiMatchDetails, setAiMatchDetails] = useState({});
    const [stats, setStats] = useState({ total: 0, active: 0, verified: 0, highScore: 0, acceptedConnections: 0 });

    // People state
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userFilters, setUserFilters] = useState({ role: '', specialization: '', region: '' });
    const [userScores, setUserScores] = useState({});
    const [userMatchDetails, setUserMatchDetails] = useState({});
    const [userStats, setUserStats] = useState({ total: 0, verified: 0, active: 0 });

    // Introduction Requests
    const [sentIntroductions, setSentIntroductions] = useState({});
    const [receivedIntroductions, setReceivedIntroductions] = useState({});
    const [introRequests, setIntroRequests] = useState([]);
    const [pendingIntroCount, setPendingIntroCount] = useState(0);

    // Invitations state
    const [receivedRequests, setReceivedRequests] = useState([]);
    const [sentRequests, setSentRequests] = useState([]);
    const [processingRequest, setProcessingRequest] = useState(false);
    const [processingInvite, setProcessingInvite] = useState(false);

    // Meetings state
    const [meetings, setMeetings] = useState([]);
    const [loadingMeetings, setLoadingMeetings] = useState(false);
    const [openMeetingDialog, setOpenMeetingDialog] = useState(false);
    const [selectedMeetingCompany, setSelectedMeetingCompany] = useState(null);
    const [meetingForm, setMeetingForm] = useState({
        title: '',
        date: '',
        time: '',
        duration: '60',
        type: 'virtual',
        location: '',
        agenda: '',
        participants: [],
    });
    const [processingMeeting, setProcessingMeeting] = useState(false);

    // Dialog states
    const [openAIDialog, setOpenAIDialog] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [aiMessage, setAiMessage] = useState('');
    const [aiSubject, setAiSubject] = useState('');
    const [aiScore, setAiScore] = useState(0);
    const [aiDetails, setAiDetails] = useState([]);
    const [openCompanyDetailDialog, setOpenCompanyDetailDialog] = useState(false);
    const [selectedCompanyDetail, setSelectedCompanyDetail] = useState(null);
    const [inviteSent, setInviteSent] = useState(false);

    // People dialogs
    const [openUserIntroDialog, setOpenUserIntroDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userIntroMessage, setUserIntroMessage] = useState('');
    const [userIntroSubject, setUserIntroSubject] = useState('');
    const [userIntroScore, setUserIntroScore] = useState(0);
    const [userIntroDetails, setUserIntroDetails] = useState([]);
    const [openUserDetailDialog, setOpenUserDetailDialog] = useState(false);
    const [selectedUserDetail, setSelectedUserDetail] = useState(null);

    // Company Users Dialog
    const [openCompanyUsersDialog, setOpenCompanyUsersDialog] = useState(false);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);
    const [selectedCompanyForUsers, setSelectedCompanyForUsers] = useState(null);

    // Snackbar for notifications
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    // ============================================
    // HELPERS
    // ============================================

    const getCompanyId = useCallback(
        () => resolveCompanyId({ companyData, userData, currentUser }),
        [companyData, userData, currentUser]
    );

    const getCompanyName = useCallback(
        () => companyData?.companyName || userData?.companyName || companyData?.name || userData?.name || 'Your Company',
        [companyData, userData]
    );

    const getCurrentUserId = useCallback(() => {
        return currentUser?.uid || userData?.uid || getCompanyId();
    }, [currentUser, userData, getCompanyId]);

    // ============================================
    // FETCH FUNCTIONS
    // ============================================

    const fetchCompanyUsers = useCallback(async (companyId) => {
        if (!companyId) return;
        setLoadingCompanyUsers(true);
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyId));
            const querySnapshot = await getDocs(q);
            const usersList = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (data.uid !== currentUser?.uid) {
                    usersList.push({ id: docSnap.id, ...data });
                }
            });
            setCompanyUsers(usersList);
        } catch (error) {
            console.error('Error fetching company users:', error);
            toast.error('Failed to load company users');
        } finally {
            setLoadingCompanyUsers(false);
        }
    }, [currentUser]);

    const fetchIntroRequests = useCallback(async () => {
        const currentUserId = getCurrentUserId();
        if (!currentUserId) return;

        try {
            const sentQuery = query(
                collection(db, 'introductions'),
                where('fromUserId', '==', currentUserId)
            );
            const sentSnapshot = await getDocs(sentQuery);
            const sentMap = {};
            const sentList = [];
            sentSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                sentMap[data.toUserId] = { id: docSnap.id, status: data.status, data };
                sentList.push({ id: docSnap.id, ...data, direction: 'sent' });
            });

            const receivedQuery = query(
                collection(db, 'introductions'),
                where('toUserId', '==', currentUserId)
            );
            const receivedSnapshot = await getDocs(receivedQuery);
            const receivedMap = {};
            const receivedList = [];
            receivedSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                receivedMap[data.fromUserId] = { id: docSnap.id, status: data.status, data };
                receivedList.push({ id: docSnap.id, ...data, direction: 'received' });
            });

            setSentIntroductions(sentMap);
            setReceivedIntroductions(receivedMap);

            const allRequests = [...sentList, ...receivedList];
            const uniqueRequests = allRequests.filter((req, index, self) => 
                index === self.findIndex(r => r.id === req.id)
            );
            setIntroRequests(uniqueRequests);

            const pendingReceived = receivedList.filter(r => r.status === 'pending');
            setPendingIntroCount(pendingReceived.length);

        } catch (error) {
            console.error('Error fetching intro requests:', error);
        }
    }, [getCurrentUserId]);

    const fetchInvitations = useCallback(async (companiesList) => {
        try {
            const companyId = getCompanyId();
            if (!companyId) return 0;

            const sentQuery = query(collection(db, 'invitations'), where('companyId', '==', companyId));
            const sentSnapshot = await getDocs(sentQuery);
            const sentList = [];
            sentSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                sentList.push({ id: docSnap.id, ...data });
            });
            setSentRequests(sentList);

            const receivedQuery = query(collection(db, 'invitations'), where('targetCompanyId', '==', companyId));
            const receivedSnapshot = await getDocs(receivedQuery);
            const receivedList = [];
            receivedSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const company = companiesList.find(c => c.id === data.companyId);
                if (company) {
                    receivedList.push({ id: docSnap.id, ...data });
                } else {
                    receivedList.push({ id: docSnap.id, ...data });
                }
            });
            setReceivedRequests(receivedList);

            const allInvitations = {};
            sentSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                allInvitations[data.targetCompanyId] = { status: data.status === 'connected' ? 'accepted' : data.status, id: docSnap.id, isSender: true };
            });
            receivedSnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                if (!allInvitations[data.companyId]) {
                    allInvitations[data.companyId] = { status: data.status === 'connected' ? 'accepted' : data.status, id: docSnap.id, isSender: false };
                }
            });

            setInvitations(allInvitations);

            const acceptedCount = Object.values(allInvitations).filter(inv => inv.status === 'accepted').length;
            setStats(prev => ({ ...prev, acceptedConnections: acceptedCount }));
            return acceptedCount;
        } catch (error) {
            console.error('Error fetching invitations:', error);
            return 0;
        }
    }, [getCompanyId]);

    const fetchMeetings = useCallback(async () => {
        setLoadingMeetings(true);
        try {
            const companyId = getCompanyId();
            if (!companyId) return;

            const meetingsRef = collection(db, 'meetings');
            const [sentSnapshot, receivedSnapshot, legacySnapshot] = await Promise.all([
                getDocs(query(meetingsRef, where('requesterId', '==', companyId))),
                getDocs(query(meetingsRef, where('targetId', '==', companyId))),
                getDocs(query(meetingsRef, where('companyId', '==', companyId))),
            ]);

            const meetingsMap = new Map();
            const addSnapshot = (snapshot, relation) => {
                snapshot.forEach((docSnap) => {
                    const existing = meetingsMap.get(docSnap.id) || {};
                    meetingsMap.set(docSnap.id, {
                        ...existing,
                        id: docSnap.id,
                        ...docSnap.data(),
                        _relation: existing._relation || relation,
                    });
                });
            };

            addSnapshot(sentSnapshot, 'sent');
            addSnapshot(receivedSnapshot, 'received');
            addSnapshot(legacySnapshot, 'legacy');

            const toMillis = (meeting) => {
                const candidate = meeting.scheduledTime || meeting.createdAt;
                if (candidate?.toDate) return candidate.toDate().getTime();
                if (candidate?.seconds) return candidate.seconds * 1000;
                if (candidate instanceof Date) return candidate.getTime();
                const raw = meeting.date || candidate;
                const parsed = raw ? new Date(raw).getTime() : 0;
                return Number.isFinite(parsed) ? parsed : 0;
            };

            const meetingsList = Array.from(meetingsMap.values()).sort(
                (a, b) => toMillis(b) - toMillis(a)
            );
            setMeetings(meetingsList);
        } catch (error) {
            console.error('Error fetching meetings:', error);
        } finally {
            setLoadingMeetings(false);
        }
    }, [getCompanyId]);

    const fetchUsers = useCallback(async () => {
        setLoadingUsers(true);
        try {
            const companyId = getCompanyId();
            const currentUserId = getCurrentUserId();
            const usersRef = collection(db, 'users');
            // Do not order the directory query by createdAt. Firestore orderBy
            // excludes legacy documents where the field is missing, which made
            // valid TELCOFAIR people disappear from the directory.
            const querySnapshot = await getDocs(usersRef);

            const usersList = [];
            querySnapshot.forEach((docSnap) => {
                const docId = docSnap.id;
                if (docId === '_schema' || docId === '_placeholder') return;
                const data = docSnap.data();
                const entityUserId = data.uid || data.userId || docId;
                if (entityUserId !== currentUserId && docId !== currentUserId && entityUserId !== companyId && docId !== companyId) {
                    usersList.push({ id: docId, uid: entityUserId, ...data });
                }
            });

            const scores = {};
            const details = {};
            const currentUserData = userData || {};

            const scored = usersList.map((user) => {
                const result = calculateUserScore(user, currentUserData);
                scores[user.id] = result.score;
                details[user.id] = result.details;
                return { ...user, aiScore: result.score };
            });

            scored.sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0));
            setUsers(scored);
            setFilteredUsers(scored);
            setUserScores(scores);
            setUserMatchDetails(details);

            setUserStats({
                total: scored.length,
                verified: scored.filter(u => u.isVerified).length,
                active: scored.filter(u => u.isActive && !u.isBlocked).length,
            });
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoadingUsers(false);
        }
    }, [getCompanyId, getCurrentUserId, userData]);

    const fetchCompanies = useCallback(async () => {
        setLoading(true);
        try {
            const companyId = getCompanyId();
            const companiesRef = collection(db, 'companies');
            // Read the complete company directory without an orderBy requirement.
            // Older company documents may not have createdAt and Firestore would
            // otherwise omit them from the result entirely.
            const querySnapshot = await getDocs(companiesRef);

            const companiesList = [];
            querySnapshot.forEach((docSnap) => {
                const docId = docSnap.id;
                if (docId === '_schema' || docId === '_placeholder') return;
                const data = docSnap.data();
                const entityCompanyId = data.companyId || docId;
                if (docId !== companyId && entityCompanyId !== companyId) {
                    companiesList.push({ id: docId, companyId: entityCompanyId, ...data });
                }
            });

            const scores = {};
            const details = {};
            const userCompany = companyData || userData || {};

            const scored = companiesList.map((company) => {
                const result = calculateCompanyScore(company, userCompany);
                scores[company.id] = result.score;
                details[company.id] = result.details;
                return { ...company, aiScore: result.score };
            });

            setCompanies(scored);
            setAiScores(scores);
            setAiMatchDetails(details);
            setFilteredCompanies([...scored].sort((a, b) => (scores[b.id] || 0) - (scores[a.id] || 0)));

            const acceptedCount = await fetchInvitations(scored);

            setStats({
                total: scored.length,
                active: scored.filter(c => c.isActive !== false && c.status !== 'inactive' && !c.isBlocked).length,
                verified: scored.filter(c => c.isVerified).length,
                highScore: Object.values(scores).filter(s => s >= 70).length,
                acceptedConnections: acceptedCount,
            });
        } catch (error) {
            console.error('Error fetching companies:', error);
            toast.error('Failed to load companies');
        } finally {
            setLoading(false);
        }
    }, [getCompanyId, currentUser, companyData, userData, fetchInvitations]);

    // ============================================
    // FILTERING - Companies
    // ============================================

    const [debouncedTerm, setDebouncedTerm] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedTerm(searchTerm), 180);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        const term = debouncedTerm.trim().toLowerCase();
        let filtered = [...companies];

        if (term) {
            filtered = filtered.filter((company) => {
                const haystack = [
                    company.companyName,
                    company.country,
                    company.city,
                    company.primaryService,
                    company.secondaryService,
                    asText(company.primaryRegion),
                    asText(company.secondaryRegion),
                    asText(company.primaryConnectWith),
                    asText(company.secondaryConnectWith),
                    company.companyType,
                ]
                    .map((v) => String(v || '').toLowerCase())
                    .join(' ');
                return haystack.includes(term);
            });
        }

        if (filters.type) {
            filtered = filtered.filter(company => company.companyType === filters.type);
        }

        if (filters.status === 'active') {
            filtered = filtered.filter(company => company.isActive && !company.isBlocked);
        } else if (filters.status === 'verified') {
            filtered = filtered.filter(company => company.isVerified);
        }

        if (filters.region) {
            filtered = filtered.filter(company =>
                asList(company.primaryRegion).includes(filters.region) ||
                asList(company.secondaryRegion).includes(filters.region)
            );
        }

        filtered.sort((a, b) => (aiScores[b.id] || 0) - (aiScores[a.id] || 0));
        setFilteredCompanies(filtered);
    }, [debouncedTerm, filters, companies, aiScores]);

    // ============================================
    // FILTERING - Users
    // ============================================

    const [debouncedUserTerm, setDebouncedUserTerm] = useState('');
    useEffect(() => {
        const t = setTimeout(() => setDebouncedUserTerm(userSearchTerm), 180);
        return () => clearTimeout(t);
    }, [userSearchTerm]);

    useEffect(() => {
        const term = debouncedUserTerm.trim().toLowerCase();
        let filtered = [...users];

        if (term) {
            filtered = filtered.filter((user) => {
                const haystack = [
                    user.displayName,
                    user.firstName,
                    user.lastName,
                    user.email,
                    user.companyName,
                    user.country,
                    user.businessTitle,
                    user.role,
                    asText(user.specialization),
                    asText(user.primaryServiceProvided),
                    asText(user.primaryRegionAvailability),
                    asText(user.secondaryServiceProvided),
                ]
                    .map((v) => String(v || '').toLowerCase())
                    .join(' ');
                return haystack.includes(term);
            });
        }

        if (userFilters.role) {
            filtered = filtered.filter(user => user.role === userFilters.role);
        }

        if (userFilters.specialization) {
            filtered = filtered.filter(user =>
                asList(user.specialization).includes(userFilters.specialization)
            );
        }

        if (userFilters.region) {
            filtered = filtered.filter(user =>
                asList(user.primaryRegionAvailability).includes(userFilters.region) ||
                asList(user.secondaryRegionAvailability).includes(userFilters.region)
            );
        }

        filtered.sort((a, b) => (userScores[b.id] || 0) - (userScores[a.id] || 0));
        setFilteredUsers(filtered);
    }, [debouncedUserTerm, userFilters, users, userScores]);

    // ============================================
    // HANDLERS - Companies
    // ============================================

    const handleSearch = useCallback((event) => setSearchTerm(event.target.value), []);
    const handleFilterChange = useCallback((field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    }, []);
    const clearSearch = useCallback(() => setSearchTerm(''), []);
    const clearFilters = useCallback(() => {
        setFilters({ type: '', status: '', region: '' });
        setSearchTerm('');
    }, []);

    // ============================================
    // HANDLERS - Users
    // ============================================

    const handleUserSearch = useCallback((event) => setUserSearchTerm(event.target.value), []);
    const handleUserFilterChange = useCallback((field, value) => {
        setUserFilters(prev => ({ ...prev, [field]: value }));
    }, []);
    const clearUserSearch = useCallback(() => setUserSearchTerm(''), []);
    const clearUserFilters = useCallback(() => {
        setUserFilters({ role: '', specialization: '', region: '' });
        setUserSearchTerm('');
    }, []);

    // ============================================
    // INTRODUCTION HANDLERS WITH NOTIFICATIONS
    // ============================================

    const handleIntroduceUser = useCallback(async (user) => {
        const currentUserData = userData || {};
        const result = calculateUserScore(user, currentUserData);
        setSelectedUser(user);
        setUserIntroScore(result.score);
        setUserIntroDetails(result.details);
        const localDraft = generatePersonalizedIntroMessage(user, currentUserData, result.score, result.details);
        setUserIntroSubject(localDraft.subject);
        setUserIntroMessage(localDraft.message);
        setOpenUserIntroDialog(true);

        const companyId = getCompanyId();
        const targetUserId = user?.uid || user?.userId || user?.id;
        if (!companyId || !targetUserId) return;
        try {
            const generated = await generateIntroduction({
                companyId,
                targetUserId,
                context: { matchScore: result.score, matchDetails: result.details, source: 'user_directory' },
                tone: 'professional',
            });
            if (generated?.text) {
                setUserIntroSubject(generated.subject || localDraft.subject);
                setUserIntroMessage(generated.text);
            }
        } catch (error) {
            console.warn('ChatGPT introduction draft unavailable; keeping local editable draft.', error);
            toast('ChatGPT AI is not configured or unavailable. The editable local draft is ready.', { icon: '✨' });
        }
    }, [userData, getCompanyId]);

    const handleSendUserIntro = useCallback(async () => {
        if (!selectedUser) { toast.error('No user selected'); return; }
        const companyId = getCompanyId();
        const targetId = selectedUser.uid || selectedUser.userId || selectedUser.id;
        if (!companyId || !targetId) { toast.error('Company or user context is unavailable.'); return; }

        const existing = sentIntroductions[targetId];
        if (existing && !['rejected', 'ignored', 'expired'].includes(String(existing.status || '').toLowerCase())) {
            toast('An active introduction already exists with this person.', { icon: 'ℹ️' });
            setOpenUserIntroDialog(false);
            return;
        }

        try {
            const result = await introductionService.send(companyId, {
                targetUserId: targetId,
                subject: userIntroSubject,
                message: userIntroMessage,
                context: { matchScore: userIntroScore, matchDetails: userIntroDetails, source: 'user_directory' },
                tone: 'professional',
            });
            setSentIntroductions(prev => ({
                ...prev,
                [targetId]: {
                    id: result.introId,
                    status: 'pending',
                    data: { aiMessage: result.message || userIntroMessage, aiSubject: userIntroSubject, chatEnabled: false, calendarShared: false }
                }
            }));
            toast.success(`Introduction sent to ${selectedUser.displayName || selectedUser.firstName || 'user'}. They decide whether to start chat or share their calendar.`);
            setOpenUserIntroDialog(false);
            await Promise.all([fetchIntroRequests(), fetchUsers()]);
        } catch (error) {
            console.error('Error sending introduction:', error);
            toast.error(friendlyError(error, 'Failed to send introduction.'));
        }
    }, [selectedUser, userIntroMessage, userIntroSubject, userIntroScore, userIntroDetails, getCompanyId, sentIntroductions, fetchIntroRequests, fetchUsers]);

    const handleRespondIntro = useCallback(async (intro, action) => {
        const companyId = getCompanyId();
        if (!companyId || !intro?.id) return;
        setProcessingRequest(true);
        try {
            const result = await introductionService.respond(companyId, intro.id, action);
            const label = action === 'chat'
                ? 'Chat enabled. The sender can now continue the conversation.'
                : action === 'calendar'
                    ? 'Calendar shared. The sender can now book one of your available slots.'
                    : action === 'both'
                        ? 'Chat and calendar access enabled.'
                        : 'Introduction ignored.';
            toast.success(label);
            setReceivedIntroductions(prev => ({
                ...prev,
                [intro.fromUserId]: {
                    ...prev[intro.fromUserId],
                    status: result?.status || (action === 'ignore' ? 'rejected' : 'accepted'),
                    data: { ...(prev[intro.fromUserId]?.data || {}), ...result }
                }
            }));
            await Promise.all([fetchIntroRequests(), fetchUsers()]);
        } catch (error) {
            console.error('Error responding to introduction:', error);
            toast.error(friendlyError(error, 'Failed to update the introduction.'));
        } finally {
            setProcessingRequest(false);
        }
    }, [getCompanyId, fetchIntroRequests, fetchUsers]);

    const handleAcceptIntro = useCallback((intro) => handleRespondIntro(intro, 'chat'), [handleRespondIntro]);
    const handleRejectIntro = useCallback((intro) => handleRespondIntro(intro, 'ignore'), [handleRespondIntro]);

    const getIntroStatusForUser = useCallback((userId) => {
        const normalize = (record, direction) => {
            if (!record) return null;
            const data = record.data || {};
            const status = record.status === 'connected' ? 'accepted' : record.status;
            return {
                status,
                direction,
                chatEnabled: Boolean(data.chatEnabled || data.recipientDecision === 'chat' || data.recipientDecision === 'both'),
                calendarShared: Boolean(data.calendarShared || data.recipientDecision === 'calendar' || data.recipientDecision === 'both'),
                recipientDecision: data.recipientDecision || '',
                introId: record.id,
            };
        };
        return normalize(sentIntroductions[userId], 'sent') || normalize(receivedIntroductions[userId], 'received') || { status: null, direction: null, chatEnabled: false, calendarShared: false };
    }, [sentIntroductions, receivedIntroductions]);

    // ============================================
    // INVITATION HANDLERS
    // ============================================

    const handleSendInvite = useCallback(async (company) => {
        if (processingInvite) {
            toast('Please wait...', { icon: '⏳' });
            return;
        }

        if (invitations[company.id]?.status === 'pending') {
            toast('Invitation already sent to ' + company.companyName, { icon: 'ℹ️', duration: 3000 });
            return;
        }

        if (invitations[company.id]?.status === 'accepted') {
            toast('Already connected with ' + company.companyName, { icon: '✅', duration: 3000 });
            return;
        }

        setProcessingInvite(true);
        try {
            const companyId = getCompanyId();
            const companyName = getCompanyName();

            const score = aiScores[company.id] || 0;
            const details = aiMatchDetails[company.id] || [];
            const aiMsg = generatePersonalizedAIMessage(company, companyData || userData || {}, score, details);

            const invitationData = {
                companyId: companyId,
                companyName: companyName,
                targetCompanyId: company.id,
                targetCompanyName: company.companyName,
                status: 'pending',
                sentAt: serverTimestamp(),
                expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                aiScore: score,
                aiMessage: aiMsg.message,
                aiSubject: aiMsg.subject,
                aiMatchDetails: details,
                autoGenerated: true,
                invitationLink: `https://telcofair.com/invite/${company.id}`
            };

            await addDoc(collection(db, 'invitations'), invitationData);

            toast.success(`AI-powered invitation sent to ${company.companyName}! Messaging unlocks once they accept.`);

            setInvitations(prev => ({ ...prev, [company.id]: { status: 'pending', id: 'new', isSender: true } }));

            setInviteSent(true);
            setTimeout(() => setInviteSent(false), 3000);

            await fetchCompanies();
        } catch (error) {
            console.error('Error sending invitation:', error);
            toast.error(friendlyError(error, 'Failed to send invitation.'));
        } finally {
            setProcessingInvite(false);
        }
    }, [processingInvite, invitations, getCompanyId, getCompanyName, aiScores, aiMatchDetails, companyData, userData, fetchCompanies]);

    const handleAcceptRequest = useCallback(async (request) => {
        if (processingRequest) {
            toast('Please wait...', { icon: '⏳' });
            return;
        }

        setProcessingRequest(true);
        try {
            const invitationRef = doc(db, 'invitations', request.id);
            const docSnap = await getDoc(invitationRef);
            if (!docSnap.exists()) {
                toast.error('Invitation no longer exists');
                return;
            }

            await updateDoc(invitationRef, {
                status: 'accepted',
                updatedAt: serverTimestamp()
            });

            toast.success('Connected with ' + request.companyName + '!');
            await fetchCompanies();
        } catch (error) {
            console.error('Error accepting request:', error);
            toast.error(friendlyError(error, 'Failed to accept request.'));
        } finally {
            setProcessingRequest(false);
        }
    }, [processingRequest, fetchCompanies]);

    const handleDeclineRequest = useCallback(async (request) => {
        if (processingRequest) {
            toast('Please wait...', { icon: '⏳' });
            return;
        }

        setProcessingRequest(true);
        try {
            const invitationRef = doc(db, 'invitations', request.id);
            const docSnap = await getDoc(invitationRef);
            if (!docSnap.exists()) {
                toast.error('Invitation no longer exists');
                return;
            }

            await updateDoc(invitationRef, {
                status: 'declined',
                updatedAt: serverTimestamp()
            });

            toast('Declined request from ' + request.companyName, { icon: '👋', duration: 3000 });
            await fetchCompanies();
        } catch (error) {
            console.error('Error declining request:', error);
            toast.error(friendlyError(error, 'Failed to decline request.'));
        } finally {
            setProcessingRequest(false);
        }
    }, [processingRequest, fetchCompanies]);

    // ============================================
    // MEETING HANDLERS
    // ============================================

    const handleScheduleMeeting = useCallback(async (user) => {
        const targetId = user?.uid || user?.userId || user?.id;
        if (!targetId || !user?.email && !user?.displayName && !user?.firstName) {
            toast('Choose a specific registered user. Company-level connections do not expose a calendar.', { icon: '🔒' });
            return;
        }
        const companyId = getCompanyId();
        try {
            const permission = await introductionService.permission(companyId, targetId);
            if (permission?.blocked) {
                toast.error(permission.reason || 'Communication with this user is restricted.');
                return;
            }
            if (!permission?.calendarShared) {
                const result = await introductionService.requestAvailability(companyId, {
                    targetUserId: targetId,
                    message: `I would like to arrange a business meeting with ${user.displayName || user.firstName || 'you'}. Please choose and share the dates and times that work for you.`,
                    agenda: `Discuss potential business opportunities with ${user.displayName || user.companyName || 'the contact'}`,
                    duration: 30,
                });
                toast.success(result?.alreadyRequested ? 'A meeting availability request is already pending.' : 'Meeting availability request sent by email and notification. The recipient can now choose which calendar windows to share.');
                return;
            }
        } catch (error) {
            toast.error(friendlyError(error, 'Could not verify calendar permission.'));
            return;
        }

        setSelectedMeetingCompany(user);
        setMeetingForm({
            title: `Meeting with ${user.companyName || user.displayName || user.firstName || 'Contact'}`,
            date: new Date(Date.now() + 86400000).toISOString().split('T')[0],
            time: '10:00',
            duration: '30',
            type: 'virtual',
            location: '',
            agenda: `Discuss potential business opportunities with ${user.displayName || user.companyName || 'the contact'}`,
            participants: [],
        });
        setOpenMeetingDialog(true);
    }, [getCompanyId]);

    const handleMeetingFormChange = (field, value) => {
        setMeetingForm(prev => ({ ...prev, [field]: value }));
    };

    const handleCreateMeeting = useCallback(async () => {
        if (!meetingForm.title || !meetingForm.date || !meetingForm.time) { toast.error('Please fill in all required fields'); return; }
        const companyId = getCompanyId();
        const targetUserId = selectedMeetingCompany?.uid || selectedMeetingCompany?.userId || selectedMeetingCompany?.id;
        if (!companyId || !targetUserId) { toast.error('A registered user is required for meeting booking.'); return; }
        setProcessingMeeting(true);
        try {
            const scheduledTime = new Date(`${meetingForm.date}T${meetingForm.time}:00`);
            if (Number.isNaN(scheduledTime.getTime()) || scheduledTime <= new Date()) { toast.error('Choose a future meeting time.'); return; }
            const result = await meetingWorkflowService.book(companyId, {
                targetUserId,
                scheduledTime: scheduledTime.toISOString(),
                duration: parseInt(meetingForm.duration, 10) || 30,
                type: meetingForm.type === 'virtual' ? 'video' : meetingForm.type,
                title: meetingForm.title,
                agenda: meetingForm.agenda,
                message: meetingForm.agenda || `Meeting booked by ${getCompanyName()}`,
            });
            toast.success('Meeting booked and confirmed. Both participants have been notified.');
            setOpenMeetingDialog(false);
            await fetchMeetings();
            return result;
        } catch (error) {
            console.error('Error creating meeting:', error);
            toast.error(friendlyError(error, 'Failed to book meeting.'));
        } finally {
            setProcessingMeeting(false);
        }
    }, [meetingForm, selectedMeetingCompany, getCompanyId, getCompanyName, fetchMeetings]);

    const handleMeetingAction = useCallback(async (meetingId, action) => {
        try {
            const meetingRef = doc(db, 'meetings', meetingId);
            await updateDoc(meetingRef, {
                status: action === 'accept' ? 'accepted' : 'declined',
                updatedAt: serverTimestamp(),
            });
            toast.success(`Meeting ${action === 'accept' ? 'accepted' : 'declined'} successfully`);
            await fetchMeetings();
        } catch (error) {
            console.error('Error updating meeting:', error);
            toast.error('Failed to update meeting');
        }
    }, [fetchMeetings]);

    // ============================================
    // OTHER HANDLERS
    // ============================================

    const handleAIConnect = useCallback((company) => {
        const userCompany = companyData || userData || {};
        const result = calculateCompanyScore(company, userCompany);

        setSelectedCompany(company);
        setAiScore(result.score);
        setAiDetails(result.details);

        const aiMsg = generatePersonalizedAIMessage(company, userCompany, result.score, result.details);
        setAiSubject(aiMsg.subject);
        setAiMessage(aiMsg.message);
        setOpenAIDialog(true);
    }, [companyData, userData]);

    const handleSendAIMessage = useCallback(async () => {
        if (!selectedCompany) {
            toast.error('No company selected');
            return;
        }
        await handleSendInvite(selectedCompany);
        setOpenAIDialog(false);
    }, [selectedCompany, handleSendInvite]);

    const handleViewCompanyDetails = useCallback((company) => {
        setSelectedCompanyDetail(company);
        setOpenCompanyDetailDialog(true);
    }, []);

    const handleOpenChat = useCallback((company) => {
        if (invitations[company.id]?.status !== 'accepted') {
            toast('Chat unlocks once they accept your AI intro.', { icon: '🔒' });
            return;
        }
        navigate('/dashboard/messages', { state: { company: company, openChat: true } });
    }, [navigate, invitations]);

    const handleViewUserDetails = useCallback((user) => {
        setSelectedUserDetail(user);
        setOpenUserDetailDialog(true);
    }, []);

    const handleOpenChatWithUser = useCallback((user) => {
        const targetId = user?.uid || user?.id;
        const introStatus = getIntroStatusForUser(targetId);
        if (introStatus.status !== 'accepted') {
            toast('Messages unlock once they accept your introduction.', { icon: '🔒' });
            return;
        }
        navigate('/dashboard/messages', { state: { company: user, openChat: true } });
    }, [navigate, getIntroStatusForUser]);

    const handleViewCompanyUsers = useCallback((company) => {
        setSelectedCompanyForUsers(company);
        fetchCompanyUsers(company.id);
        setOpenCompanyUsersDialog(true);
    }, [fetchCompanyUsers]);

    const handleSendIntroToCompanyUser = useCallback((user) => {
        handleIntroduceUser(user);
    }, [handleIntroduceUser]);

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        const companyId = getCompanyId();

        // The directory itself is readable independently of invitation state.
        // Load it immediately even while auth/company context is finishing its
        // migration/bootstrap, instead of rendering a permanently empty page.
        fetchCompanies();
        fetchUsers();
        fetchMeetings();
        fetchIntroRequests();

        if (!companyId) return undefined;

        const invitationsRef = collection(db, 'invitations');
        const receivedInvQ = query(invitationsRef, where('targetCompanyId', '==', companyId));
        const sentInvQ = query(invitationsRef, where('companyId', '==', companyId));
        const unsubscribeReceivedInvitations = onSnapshot(receivedInvQ, () => { fetchCompanies(); });
        const unsubscribeSentInvitations = onSnapshot(sentInvQ, () => { fetchCompanies(); });

        const meetingsRef = collection(db, 'meetings');
        const sentMeetingsQ = query(meetingsRef, where('requesterId', '==', companyId));
        const receivedMeetingsQ = query(meetingsRef, where('targetId', '==', companyId));
        const legacyMeetingsQ = query(meetingsRef, where('companyId', '==', companyId));
        const unsubscribeSentMeetings = onSnapshot(sentMeetingsQ, () => { fetchMeetings(); });
        const unsubscribeReceivedMeetings = onSnapshot(receivedMeetingsQ, () => { fetchMeetings(); });
        const unsubscribeLegacyMeetings = onSnapshot(legacyMeetingsQ, () => { fetchMeetings(); });

        const usersRef = collection(db, 'users');
        const unsubscribeUsers = onSnapshot(usersRef, () => { fetchUsers(); });

        const currentUserId = getCurrentUserId();
        if (currentUserId) {
            const introsRef = collection(db, 'introductions');
            
            const sentIntrosQ = query(introsRef, where('fromUserId', '==', currentUserId));
            const unsubscribeSentIntros = onSnapshot(sentIntrosQ, () => { 
                fetchIntroRequests();
                fetchUsers();
            });

            const receivedIntrosQ = query(introsRef, where('toUserId', '==', currentUserId));
            const unsubscribeReceivedIntros = onSnapshot(receivedIntrosQ, () => {
                fetchIntroRequests();
                fetchUsers();
            });

            return () => {
                unsubscribeReceivedInvitations();
                unsubscribeSentInvitations();
                unsubscribeSentMeetings();
                unsubscribeReceivedMeetings();
                unsubscribeLegacyMeetings();
                unsubscribeUsers();
                unsubscribeSentIntros();
                unsubscribeReceivedIntros();
            };
        }

        return () => {
            unsubscribeReceivedInvitations();
            unsubscribeSentInvitations();
            unsubscribeSentMeetings();
            unsubscribeReceivedMeetings();
            unsubscribeLegacyMeetings();
            unsubscribeUsers();
        };
    }, [getCompanyId, getCurrentUserId]);

    // ============================================
    // DERIVED
    // ============================================

    const statsData = useMemo(() => [
        { title: 'Total Companies', value: stats.total, icon: <BusinessIcon />, overlayIcon: <BusinessIcon />, color: palette.blue },
        { title: 'Active', value: stats.active, icon: <CheckCircleIcon />, overlayIcon: <CheckCircleIcon />, color: palette.green },
        { title: 'AI Matches', value: stats.highScore, icon: <AiIcon />, overlayIcon: <AiIcon />, color: palette.purple },
        { title: 'Connected', value: stats.acceptedConnections, icon: <PeopleIcon />, overlayIcon: <GroupIcon />, color: palette.teal },
    ], [stats]);

    const userStatsData = useMemo(() => [
        { title: 'Total People', value: userStats.total, icon: <PersonIcon />, overlayIcon: <GroupIcon />, color: palette.rust },
        { title: 'Verified', value: userStats.verified, icon: <VerifiedIcon />, overlayIcon: <VerifiedIcon />, color: palette.pine },
        { title: 'Active', value: userStats.active, icon: <LiveIcon />, overlayIcon: <LiveIcon />, color: palette.teal },
        { title: 'AI Matches', value: Object.values(userScores).filter(s => s >= 60).length, icon: <AiIcon />, overlayIcon: <AiIcon />, color: palette.purple },
    ], [userStats, userScores]);

    const pendingReceived = useMemo(
        () => receivedRequests.filter(inv => inv.status === 'pending'),
        [receivedRequests]
    );

    const pendingSent = useMemo(
        () => sentRequests.filter(inv => inv.status === 'pending'),
        [sentRequests]
    );

    const acceptedConnections = useMemo(() => {
        const rows = [...receivedRequests, ...sentRequests].filter((inv) => ['accepted', 'connected'].includes(inv.status));
        const seen = new Set();
        return rows.filter((inv) => {
            const otherId = inv.targetCompanyId || inv.companyId || inv.senderCompanyId || inv.id;
            const key = String(otherId || inv.id || '');
            if (!key || seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }, [receivedRequests, sentRequests]);

    const meetingSortValue = useCallback((meeting) => {
        if (!meeting) return Number.MAX_SAFE_INTEGER;

        const candidates = [
            meeting.scheduledTime,
            meeting.startTime,
            meeting.dateTime,
            meeting.meetingDate,
            meeting.date,
            meeting.createdAt,
        ];

        for (const value of candidates) {
            if (!value) continue;
            if (typeof value?.toMillis === 'function') {
                const millis = value.toMillis();
                if (Number.isFinite(millis)) return millis;
            }
            if (typeof value?.toDate === 'function') {
                const millis = value.toDate()?.getTime?.();
                if (Number.isFinite(millis)) return millis;
            }
            if (value instanceof Date) {
                const millis = value.getTime();
                if (Number.isFinite(millis)) return millis;
            }
            if (typeof value === 'number' && Number.isFinite(value)) return value;
            if (typeof value === 'string' && value.trim()) {
                const raw = value.trim();
                const withTime = meeting.time && /^\d{4}-\d{2}-\d{2}$/.test(raw)
                    ? `${raw}T${meeting.time}`
                    : raw;
                const millis = Date.parse(withTime);
                if (Number.isFinite(millis)) return millis;
            }
        }

        return Number.MAX_SAFE_INTEGER;
    }, []);

    const upcomingMeetings = useMemo(
        () => meetings
            .filter(m => m.status === 'scheduled' || m.status === 'confirmed' || m.status === 'accepted')
            .slice()
            .sort((a, b) => {
                const diff = meetingSortValue(a) - meetingSortValue(b);
                if (diff !== 0) return diff;
                return String(a?.id || a?.title || '').localeCompare(String(b?.id || b?.title || ''));
            }),
        [meetings, meetingSortValue]
    );

    const pastMeetings = useMemo(
        () => meetings.filter(m => m.status === 'completed' || m.status === 'declined'),
        [meetings]
    );

    const allRegions = useMemo(
        () => [...new Set(companies.flatMap(c => [...asList(c.primaryRegion), ...asList(c.secondaryRegion)]))]
            .filter(Boolean)
            .sort(),
        [companies]
    );

    const allUserSpecializations = useMemo(
        () => [...new Set(users.flatMap(u => asList(u.specialization)))]
            .filter(Boolean)
            .sort(),
        [users]
    );

    const allUserRegions = useMemo(
        () => [...new Set(users.flatMap(u => [...asList(u.primaryRegionAvailability), ...asList(u.secondaryRegionAvailability)]))]
            .filter(Boolean)
            .sort(),
        [users]
    );

    const userRoles = useMemo(
        () => [...new Set(users.map(u => u.role).filter(Boolean))],
        [users]
    );

    const hasActiveFilter = Boolean(filters.type || filters.status || filters.region || searchTerm);
    const hasUserActiveFilter = Boolean(userFilters.role || userFilters.specialization || userFilters.region || userSearchTerm);

    const selectSx = {
        ...glassWell('12px'),
        fontSize: '0.8rem',
        '& fieldset': { border: 'none' },
        '& .MuiSelect-select': { py: 1.2 },
    };

    // ============================================
    // FORMAT HELPERS
    // ============================================

    const formatDateTime = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleString('en-US', { 
            month: 'short', 
            day: 'numeric', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // ============================================================================
    // RENDER COMPANIES TAB
    // ============================================================================

    const renderCompaniesTab = () => (
        <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
                <Paper elevation={0} sx={{
                    ...glassCard({ level: 1, radius: '14px', accent: palette.rust }),
                    p: { xs: 2.5, md: 3 },
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                    '&:focus-within': { borderColor: alpha(palette.rust, .13) },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '6%', right: '6%', height: '2px',
                        borderRadius: '0 0 999px 999px',
                        background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.6)}, ${alpha(palette.gold, 0.6)}, ${alpha(palette.pine, 0.6)}, ${alpha(palette.purple, 0.6)}, transparent)`,
                        backgroundSize: '300% 100%',
                        animation: 'none',
                        pointerEvents: 'none',
                    },
                }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search by name, service, region, type…"
                            value={searchTerm}
                            onChange={handleSearch}
                            sx={{
                                flex: '1 1 260px',
                                minWidth: '200px',
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    fontSize: '0.85rem',
                                    transition: `all 0.3s ${EASE}`,
                                    '& fieldset': { border: 'none' },
                                    '&.Mui-focused': {
                                        borderColor: alpha(palette.rust, 0.5),
                                        boxShadow: `inset 0 1px 6px rgba(${SHADOW},0.06), 0 0 0 3px ${alpha(palette.rust, 0.14)}`,
                                    },
                                    '& input': { py: 1.3, color: palette.ink },
                                },
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearSearch} aria-label="Clear search">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: '145px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Type</InputLabel>
                            <Select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                label="Type"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                {COMPANY_TYPES.map(type => (
                                    <MenuItem key={type} value={type}>{type}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: '135px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
                            <Select
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                                label="Status"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Status</MenuItem>
                                <MenuItem value="active">Active</MenuItem>
                                <MenuItem value="verified">Verified</MenuItem>
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: '145px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Region</InputLabel>
                            <Select
                                value={filters.region}
                                onChange={(e) => handleFilterChange('region', e.target.value)}
                                label="Region"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Regions</MenuItem>
                                {allRegions.map(region => (
                                    <MenuItem key={region} value={region}>{region}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <AnimatePresence>
                            {hasActiveFilter && (
                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                                    <Button
                                        size="small"
                                        startIcon={<ClearIcon />}
                                        onClick={clearFilters}
                                        sx={{ ...glassGhostButton(palette.rust), px: 1.75, py: 0.8, fontSize: '0.75rem' }}
                                    >
                                        Clear All
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.65rem' }}>
                                Sorted by AI Score
                            </Typography>
                            <Box sx={{ ...glassPill(palette.purple), px: 1.1, height: 22, fontSize: '0.6rem' }}>
                                <TrendingUpIcon sx={{ fontSize: 12 }} />High to Low
                            </Box>
                        </Box>
                    </Box>

                    {!loading && (
                        <Box sx={{ ...glassCard({ level: 0, radius: '12px' }), mt: 2, px: 1.5, py: 0.9, display: 'inline-flex' }}>
                            <Typography sx={{ color: palette.inkFaint, fontSize: '0.72rem', fontWeight: 700 }}>
                                Showing {filteredCompanies.length} of {companies.length} compan{companies.length === 1 ? 'y' : 'ies'}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            <Box sx={{ mt: 1.25 }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                    gap: 1.15,
                    width: '100%',
                }}>
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={`sk-${i}`} />)
                        : filteredCompanies.map((company) => {
                            const colors = [palette.rust, palette.pine, palette.teal, palette.gold, palette.purple];
                            const idx = Number.isFinite(company.id?.length) ? company.id.length % colors.length : 0;
                            const accentColor = colors[idx];
                            const accentGradient =
                                accentColor === palette.rust ? `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})` :
                                accentColor === palette.pine ? `linear-gradient(135deg, ${palette.pine}, ${palette.pineDeep})` :
                                accentColor === palette.teal ? `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})` :
                                accentColor === palette.gold ? `linear-gradient(135deg, ${palette.gold}, ${palette.goldDeep})` :
                                `linear-gradient(135deg, ${palette.purple}, ${palette.navy})`;

                            return (
                                <PremiumCompanyCard
                                    key={company.id}
                                    company={{
                                        ...company,
                                        invitationStatus: invitations[company.id]?.status,
                                        invitationIsSender: invitations[company.id]?.isSender,
                                    }}
                                    accentColor={accentColor}
                                    accentGradient={accentGradient}
                                    onClick={() => handleViewCompanyDetails(company)}
                                    onAIConnect={handleAIConnect}
                                    onChat={handleOpenChat}
                                />
                            );
                        })}
                </Box>

                {!loading && filteredCompanies.length === 0 && (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <Box sx={{ ...glassCard({ level: 1, radius: '14px' }), textAlign: 'center', py: 7, px: 3, mt: 1 }}>
                            <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {[0, 1].map((i) => (
                                    <Box key={i} sx={{
                                        position: 'absolute', width: 72, height: 72, borderRadius: '50%',
                                        border: `1.5px solid ${palette.inkFaint}`, opacity: 0,
                                        animation: `tfSignalWave 3.2s ease-out ${i * 1.1}s infinite`,
                                    }} />
                                ))}
                                <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), opacity: 0.85 }}>
                                    <BusinessIcon sx={{ fontSize: 34 }} />
                                </Box>
                            </Box>
                            <Typography variant="h6" sx={{ color: palette.ink, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.25rem' }}>
                                No companies found
                            </Typography>
                            <Typography variant="body2" sx={{ color: palette.inkFaint, fontSize: '0.9rem', mt: 0.5 }}>
                                {hasActiveFilter ? 'Try adjusting your search or filters' : 'No other companies are on the platform yet'}
                            </Typography>
                            {hasActiveFilter && (
                                <Button onClick={clearFilters} startIcon={<ClearIcon />} sx={{ ...glassGhostButton(palette.rust), mt: 2.5, px: 3, py: 1 }}>
                                    Clear filters
                                </Button>
                            )}
                        </Box>
                    </motion.div>
                )}
            </Box>
        </>
    );

    const findUserRecord = useCallback((id, name = '') => {
        const key = String(id || '');
        const normalizedName = String(name || '').trim().toLowerCase();
        return users.find((row) => String(row.id || row.uid || row.userId || '') === key) ||
            users.find((row) => normalizedName && String(row.displayName || [row.firstName, row.lastName].filter(Boolean).join(' ') || row.name || '').trim().toLowerCase() === normalizedName) || null;
    }, [users]);

    const findCompanyRecord = useCallback((id, name = '', person = null) => {
        const keys = [id, person?.companyId, person?.company?.id, person?.company?.companyId].filter(Boolean).map(String);
        const normalizedName = String(name || person?.companyName || person?.company || '').trim().toLowerCase();
        return companies.find((row) => keys.includes(String(row.id || row.companyId || ''))) ||
            companies.find((row) => normalizedName && String(row.companyName || row.name || '').trim().toLowerCase() === normalizedName) || null;
    }, [companies]);

    // ============================================================================
    // RENDER INVITATIONS TAB
    // ============================================================================

    const renderInvitationsTab = () => {
        const receivedIntros = introRequests.filter((row) => row.direction === 'received');
        const sentIntros = introRequests.filter((row) => row.direction === 'sent');

        const sectionTitle = (icon, title, helper, count, color) => (
            <Box sx={{ display:'flex',alignItems:'center',gap:1,mb:1.25,minWidth:0 }}>
                <Box sx={{ ...glassPod(color,34,'10px'), '& svg':{fontSize:17} }}>{icon}</Box>
                <Box sx={{ minWidth:0,flex:1 }}>
                    <Typography sx={{ fontSize:'.8rem',fontWeight:850,color:palette.ink,lineHeight:1.15 }}>{title}</Typography>
                    <Typography sx={{ mt:.18,fontSize:'.53rem',color:palette.inkFaint }} noWrap>{helper}</Typography>
                </Box>
                {Number.isFinite(count) && <Box sx={{ ...glassPill(color, true),height:21,minWidth:25,justifyContent:'center',fontSize:'.5rem' }}>{count}</Box>}
            </Box>
        );

        const empty = (icon, text, color) => (
            <Box sx={{ py:3.2,textAlign:'center',borderRadius:'14px',background:alpha(color,.025),border:`1px dashed ${alpha(color,.13)}` }}>
                <Box sx={{ ...glassPod(color,44,'50%'),mx:'auto',mb:1,opacity:.76 }}>{icon}</Box>
                <Typography sx={{ fontSize:'.68rem',fontWeight:700,color:palette.inkLight }}>{text}</Typography>
            </Box>
        );

        return (
            <Box className="tf-invitations-tab" sx={{ mt:2.1 }}>
                <Box sx={{ display:'grid',gridTemplateColumns:{xs:'1fr',lg:'1.15fr .85fr'},gap:1.25,alignItems:'start' }}>
                    <Paper elevation={0} className="tf-invitation-section" sx={{ ...glassCard({level:2,radius:'18px',accent:palette.purple}),p:1.35 }}>
                        {sectionTitle(<AiIcon/>, 'AI introductions', 'Recipient-controlled chat and calendar invitations', receivedIntros.length + sentIntros.length, palette.purple)}
                        {receivedIntros.length === 0 && sentIntros.length === 0 ? empty(<AiIcon sx={{fontSize:21}}/>, 'No AI introductions yet', palette.purple) : (
                            <Box sx={{ display:'grid',gridTemplateColumns:{xs:'1fr',md:'1fr 1fr'},gap:1 }}>
                                {receivedIntros.map((intro) => {
                                    const person = findUserRecord(intro.fromUserId || intro.senderUserId, intro.fromUserName || intro.senderName);
                                    const company = findCompanyRecord(intro.fromCompanyId || intro.senderCompanyId || person?.companyId, intro.fromCompanyName || intro.senderCompanyName, person);
                                    const statusColor = intro.status === 'accepted' ? palette.pine : intro.status === 'pending' ? palette.gold : palette.clay;
                                    return (
                                        <InvitationIdentityCard key={intro.id} person={person} company={company} title={intro.fromUserName || intro.senderName} subtitle={`${person?.jobTitle || person?.designation || 'Business professional'} · ${company?.companyName || intro.fromCompanyName || 'Company'}`} status={intro.status} statusColor={statusColor} aiScore={intro.aiScore || intro.matchScore} dateLabel={formatDateTime(intro.createdAt)} message={intro.aiMessage}>
                                            {intro.status === 'pending' && <>
                                                <Button size="small" startIcon={<ChatIcon sx={{fontSize:13}}/>} onClick={()=>handleRespondIntro(intro,'chat')} disabled={processingRequest} sx={{...glassButton(palette.pine,palette.pineDeep),fontSize:'.57rem',minHeight:29}}>Start Chat</Button>
                                                <Button size="small" startIcon={<CalendarMonthIcon sx={{fontSize:13}}/>} onClick={()=>handleRespondIntro(intro,'calendar')} disabled={processingRequest} sx={{...glassButton(palette.rust,palette.rustDeep),fontSize:'.57rem',minHeight:29}}>Share Calendar</Button>
                                                <Button size="small" startIcon={<ThumbDownIcon sx={{fontSize:13}}/>} onClick={()=>handleRespondIntro(intro,'ignore')} disabled={processingRequest} sx={{...glassGhostButton(palette.clay),fontSize:'.57rem',minHeight:29}}>Ignore</Button>
                                            </>}
                                            {intro.status === 'accepted' && <>
                                                {intro.chatEnabled && <Button size="small" startIcon={<ChatIcon sx={{fontSize:13}}/>} onClick={()=>person && handleOpenChatWithUser(person)} sx={{...glassButton(palette.gold,palette.goldDeep),fontSize:'.57rem',minHeight:29}}>Chat</Button>}
                                                {intro.calendarShared && <Button size="small" startIcon={<EventIcon sx={{fontSize:13}}/>} onClick={()=>person && handleScheduleMeeting(person)} sx={{...glassButton(palette.rust,palette.rustDeep),fontSize:'.57rem',minHeight:29}}>Book Calendar</Button>}
                                                {!intro.chatEnabled && !intro.calendarShared && <Chip size="small" label="Decision recorded" />}
                                            </>}
                                        </InvitationIdentityCard>
                                    );
                                })}
                                {sentIntros.map((intro) => {
                                    const person = findUserRecord(intro.toUserId || intro.targetUserId, intro.toUserName || intro.targetName);
                                    const company = findCompanyRecord(intro.toCompanyId || intro.targetCompanyId || person?.companyId, intro.toCompanyName || intro.targetCompanyName, person);
                                    const statusColor = intro.status === 'accepted' ? palette.pine : intro.status === 'pending' ? palette.gold : palette.clay;
                                    return (
                                        <InvitationIdentityCard key={intro.id} person={person} company={company} title={intro.toUserName || intro.targetName} subtitle={`${person?.jobTitle || person?.designation || 'Business professional'} · ${company?.companyName || intro.toCompanyName || 'Company'}`} status={intro.status} statusColor={statusColor} aiScore={intro.aiScore || intro.matchScore} dateLabel={`Sent · ${formatDateTime(intro.createdAt)}`} message={intro.aiMessage}>
                                            {intro.status === 'pending' && <Button size="small" startIcon={<CancelIcon sx={{fontSize:13}}/>} onClick={async()=>{try{await deleteDoc(doc(db,'introductions',intro.id));toast.success('Introduction request cancelled');await fetchIntroRequests();}catch(error){toast.error('Failed to cancel request');}}} sx={{...glassGhostButton(palette.clay),fontSize:'.57rem',minHeight:29}}>Cancel</Button>}
                                            {intro.status === 'accepted' && <>
                                                {intro.chatEnabled && <Button size="small" startIcon={<ChatIcon sx={{fontSize:13}}/>} onClick={()=>person && handleOpenChatWithUser(person)} sx={{...glassButton(palette.gold,palette.goldDeep),fontSize:'.57rem',minHeight:29}}>Chat</Button>}
                                                {intro.calendarShared && <Button size="small" startIcon={<EventIcon sx={{fontSize:13}}/>} onClick={()=>person && handleScheduleMeeting(person)} sx={{...glassButton(palette.rust,palette.rustDeep),fontSize:'.57rem',minHeight:29}}>Book Calendar</Button>}
                                            </>}
                                        </InvitationIdentityCard>
                                    );
                                })}
                            </Box>
                        )}
                    </Paper>

                    <Box sx={{ display:'grid',gap:1.25 }}>
                        <Paper elevation={0} className="tf-invitation-section" sx={{ ...glassCard({level:2,radius:'18px',accent:palette.gold}),p:1.35 }}>
                            {sectionTitle(<InboxIcon/>, 'Received invitations', 'Company connection requests waiting for you', pendingReceived.length, palette.gold)}
                            {pendingReceived.length === 0 ? empty(<InboxIcon sx={{fontSize:21}}/>, 'No pending company invitations', palette.gold) : (
                                <Box sx={{ display:'grid',gap:1 }}>
                                    {pendingReceived.map((request) => {
                                        const company = findCompanyRecord(request.companyId || request.senderCompanyId, request.companyName || request.senderCompanyName);
                                        return <InvitationIdentityCard key={request.id} company={company} title={request.companyName || company?.companyName || 'Company'} subtitle={company?.primaryService || company?.companyType || 'Company invitation'} status="pending" statusColor={palette.gold} aiScore={request.aiScore} dateLabel={formatDateTime(request.createdAt)} message={request.aiMessage}>
                                            <Button size="small" startIcon={<ThumbUpIcon sx={{fontSize:13}}/>} onClick={()=>handleAcceptRequest(request)} disabled={processingRequest} sx={{...glassButton(palette.pine,palette.pineDeep),fontSize:'.57rem',minHeight:29}}>Accept</Button>
                                            <Button size="small" startIcon={<ThumbDownIcon sx={{fontSize:13}}/>} onClick={()=>handleDeclineRequest(request)} disabled={processingRequest} sx={{...glassGhostButton(palette.clay),fontSize:'.57rem',minHeight:29}}>Decline</Button>
                                        </InvitationIdentityCard>;
                                    })}
                                </Box>
                            )}
                        </Paper>

                        <Paper elevation={0} className="tf-invitation-section" sx={{ ...glassCard({level:2,radius:'18px',accent:palette.teal}),p:1.35 }}>
                            {sectionTitle(<SendIcon/>, 'Sent invitations', 'Track company invitations and accepted connections', sentRequests.length, palette.teal)}
                            {sentRequests.length === 0 ? empty(<SendIcon sx={{fontSize:21}}/>, 'No company invitations sent yet', palette.teal) : (
                                <Box sx={{ display:'grid',gap:1 }}>
                                    {sentRequests.map((request) => {
                                        const company = findCompanyRecord(request.targetCompanyId, request.targetCompanyName);
                                        const statusColor = ['accepted','connected'].includes(request.status) ? palette.pine : request.status === 'declined' ? palette.clay : palette.teal;
                                        return <InvitationIdentityCard key={request.id} company={company} title={request.targetCompanyName || company?.companyName || 'Company'} subtitle={company?.primaryService || company?.companyType || 'Company invitation'} status={request.status} statusColor={statusColor} aiScore={request.aiScore} dateLabel={formatDateTime(request.createdAt)} message={request.status === 'pending' ? request.aiMessage : ''}>
                                            {request.status === 'pending' && <Button size="small" startIcon={<CancelIcon sx={{fontSize:13}}/>} onClick={async()=>{try{await deleteDoc(doc(db,'invitations',request.id));toast.success('Invitation cancelled');await fetchCompanies();}catch(error){toast.error('Failed to cancel invitation');}}} sx={{...glassGhostButton(palette.clay),fontSize:'.57rem',minHeight:29}}>Cancel</Button>}
                                            {['accepted','connected'].includes(request.status) && <Button size="small" startIcon={<ChatIcon sx={{fontSize:13}}/>} onClick={()=>company && handleOpenChat(company)} sx={{...glassButton(palette.gold,palette.goldDeep),fontSize:'.57rem',minHeight:29}}>Chat</Button>}
                                        </InvitationIdentityCard>;
                                    })}
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </Box>

                <Paper elevation={0} className="tf-connections-section" sx={{ ...glassCard({level:2,radius:'18px',accent:palette.pine}),p:1.35,mt:1.25 }}>
                    {sectionTitle(<HandshakeIcon/>, 'Active connections', 'Accepted company relationships ready for chat, introductions and meetings', acceptedConnections.length, palette.pine)}
                    {acceptedConnections.length === 0 ? empty(<HandshakeIcon sx={{fontSize:21}}/>, 'No accepted company connections yet', palette.pine) : (
                        <Box sx={{ display:'grid',gridTemplateColumns:{xs:'1fr',sm:'1fr 1fr',lg:'repeat(3,1fr)'},gap:1 }}>
                            {acceptedConnections.map((request) => {
                                const targetId = request.targetCompanyId || request.companyId || request.senderCompanyId;
                                const targetName = request.targetCompanyName || request.companyName || request.senderCompanyName;
                                const company = findCompanyRecord(targetId, targetName);
                                const connectionPersonId = request.targetUserId || request.senderUserId || request.userId || request.requesterUserId || '';
                                const connectionPerson = findUserRecord(connectionPersonId);
                                const connectionTitle = connectionPerson ? (connectionPerson.displayName || [connectionPerson.firstName, connectionPerson.lastName].filter(Boolean).join(' ') || connectionPerson.name) : (company?.companyName || targetName || 'Connected company');
                                const connectionSubtitle = connectionPerson ? `${connectionPerson.jobTitle || connectionPerson.designation || connectionPerson.role || 'Business professional'} · ${company?.companyName || targetName || 'Company'}` : (company?.primaryService || company?.companyType || 'Active connection');
                                return <InvitationIdentityCard key={`connection-${request.id}`} person={connectionPerson} company={company} title={connectionTitle} subtitle={connectionSubtitle} status="connected" statusColor={palette.pine} aiScore={request.aiScore}>
                                    {company && <Button size="small" startIcon={<VisibilityIcon sx={{fontSize:13}}/>} onClick={()=>handleViewCompanyDetails(company)} sx={{...glassGhostButton(palette.pine),fontSize:'.57rem',minHeight:29}}>View Company</Button>}
                                    {company && <Button size="small" startIcon={<ChatIcon sx={{fontSize:13}}/>} onClick={()=>handleOpenChat(company)} sx={{...glassButton(palette.gold,palette.goldDeep),fontSize:'.57rem',minHeight:29}}>Chat</Button>}
                                    {company && <Button size="small" startIcon={<PeopleIcon sx={{fontSize:13}}/>} onClick={()=>handleViewCompanyUsers(company)} sx={{...glassButton(palette.rust,palette.rustDeep),fontSize:'.57rem',minHeight:29}}>People</Button>}
                                </InvitationIdentityCard>;
                            })}
                        </Box>
                    )}
                </Paper>
            </Box>
        );
    };

    // ============================================================================
    // RENDER MEETINGS TAB
    // ============================================================================

    const renderMeetingsTab = () => {
        const confirmedCount = meetings.filter(m => ['accepted', 'confirmed'].includes(String(m.status || '').toLowerCase())).length;
        const awaitingCount = meetings.filter(m => ['scheduled', 'pending'].includes(String(m.status || '').toLowerCase())).length;

        const summaryChips = [
            { label: 'Upcoming', value: upcomingMeetings.length, color: palette.rust, icon: <CalendarMonthIcon sx={{ fontSize: 15 }} /> },
            { label: 'Confirmed', value: confirmedCount, color: palette.pine, icon: <CheckCircleIcon sx={{ fontSize: 15 }} /> },
            { label: 'Awaiting reply', value: awaitingCount, color: palette.gold, icon: <PendingIcon sx={{ fontSize: 15 }} /> },
            { label: 'Completed', value: pastMeetings.length, color: palette.teal, icon: <HistoryIcon sx={{ fontSize: 15 }} /> },
        ];

        const handleJoin = (meeting) => {
            if (meeting.meetingLink) {
                openSafeExternalUrl(meeting.meetingLink);
                return;
            }
            // react-hot-toast has no .info() — the previous call threw silently.
            toast('The organiser has not attached a joining link yet.', { icon: '🔗' });
        };

        return (
            <Box sx={{ mt: 2.1 }}>
                {/* ---------- COMMAND BAR ---------- */}
                <Paper elevation={0} sx={{
                    ...glassCard({ level: 2, radius: '18px', accent: palette.rust }),
                    p: 1.5,
                    mb: 1.35,
                    '&::before': {
                        content: '""', position: 'absolute', top: 0, left: '6%', right: '6%', height: '2px',
                        borderRadius: '0 0 999px 999px',
                        background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, .6)}, ${alpha(palette.gold, .6)}, ${alpha(palette.pine, .6)}, ${alpha(palette.purple, .6)}, transparent)`,
                        backgroundSize: '300% 100%',
                        animation: 'tfEdgeShift 12s linear infinite',
                        pointerEvents: 'none',
                    },
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, flexWrap: 'wrap' }}>
                        {/* icon inherits getContrastText from glassPod — no accent-on-accent text */}
                        <Box sx={{ ...glassPod(palette.rust, 38, '11px'), '& svg': { fontSize: 19 } }}>
                            <EventIcon />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography sx={{ fontSize: '.82rem', fontWeight: 860, color: palette.ink, lineHeight: 1.15, letterSpacing: '-.02em' }}>
                                Meetings
                            </Typography>
                            <Typography sx={{ mt: .18, fontSize: '.55rem', color: palette.inkFaint }} noWrap>
                                Booked through accepted introductions and shared calendars.
                            </Typography>
                        </Box>
                        <Button
                            startIcon={<PeopleIcon sx={{ fontSize: 15 }} />}
                            onClick={() => changeWorkspaceTab(3)}
                            sx={{ ...glassGhostButton(palette.teal), minHeight: 31, px: 1.4, fontSize: '.62rem' }}
                        >
                            Find people
                        </Button>
                        <Button
                            startIcon={<AddIcon sx={{ fontSize: 15 }} />}
                            onClick={() => setOpenMeetingDialog(true)}
                            sx={{ ...glassButton(palette.rust, palette.rustDeep), minHeight: 31, px: 1.6, fontSize: '.62rem' }}
                        >
                            Schedule
                        </Button>
                    </Box>

                    <Box sx={{ mt: 1.15, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: .7 }}>
                        {summaryChips.map((chip) => (
                            <Box key={chip.label} sx={{
                                position: 'relative', overflow: 'hidden',
                                display: 'flex', alignItems: 'center', gap: .75,
                                px: .9, py: .7, borderRadius: '12px',
                                background: `linear-gradient(135deg, ${alpha(chip.color, .085)}, ${alpha(chip.color, .02)})`,
                                border: `1px solid ${alpha(chip.color, .12)}`,
                            }}>
                                <Box sx={{ position: 'absolute', width: 54, height: 54, right: -22, bottom: -26, borderRadius: '50%', bgcolor: alpha(chip.color, .06) }} />
                                <Box sx={{ ...glassPod(chip.color, 26, '8px') }}>{chip.icon}</Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '1rem', fontWeight: 880, color: palette.ink, lineHeight: 1, letterSpacing: '-.04em' }}>
                                        {chip.value}
                                    </Typography>
                                    <Typography sx={{ fontSize: '.47rem', fontWeight: 850, textTransform: 'uppercase', letterSpacing: '.07em', color: palette.inkFaint }} noWrap>
                                        {chip.label}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Paper>

                {/* ---------- BODY ---------- */}
                {loadingMeetings ? (
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.15 }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Box key={`mtg-sk-${i}`} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 1.6, minHeight: 168 }}>
                                <Box sx={{ display: 'flex', gap: 1.25 }}>
                                    <GlassSkeleton h={72} w={62} radius="15px" />
                                    <Box sx={{ flex: 1 }}>
                                        <GlassSkeleton h={13} w="72%" mb={1} />
                                        <GlassSkeleton h={10} w="48%" mb={1} />
                                        <GlassSkeleton h={20} w="88%" radius="999px" />
                                    </Box>
                                </Box>
                                <GlassSkeleton h={44} w="100%" radius="11px" mb={1.2} />
                                <GlassSkeleton h={29} w="55%" radius="9px" />
                            </Box>
                        ))}
                    </Box>
                ) : meetings.length === 0 ? (
                    <Box sx={{ ...glassCard({ level: 1, radius: '18px', accent: palette.rust }), textAlign: 'center', py: 6.5, px: 3 }}>
                        <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {[0, 1].map((i) => (
                                <Box key={i} sx={{
                                    position: 'absolute', width: 72, height: 72, borderRadius: '50%',
                                    border: `1.5px solid ${alpha(palette.rust, .5)}`, opacity: 0,
                                    animation: `tfSignalWave 3.2s ease-out ${i * 1.1}s infinite`,
                                }} />
                            ))}
                            <Box sx={{ ...glassPod(palette.rust, 72, '50%') }}>
                                <CalendarMonthIcon sx={{ fontSize: 32 }} />
                            </Box>
                        </Box>
                        <Typography sx={{ fontSize: '1.05rem', fontWeight: 850, color: palette.ink }}>
                            No meetings on the calendar
                        </Typography>
                        <Typography sx={{ mt: .4, fontSize: '.75rem', color: palette.inkFaint, maxWidth: 420, mx: 'auto', lineHeight: 1.55 }}>
                            Send an AI introduction first — once someone shares their calendar you can book a slot directly from their profile.
                        </Typography>
                        <Box sx={{ mt: 2.2, display: 'flex', gap: .8, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button
                                startIcon={<PeopleIcon sx={{ fontSize: 16 }} />}
                                onClick={() => changeWorkspaceTab(3)}
                                sx={{ ...glassButton(palette.teal, palette.tealDeep), px: 2.4, py: .95, fontSize: '.72rem' }}
                            >
                                Browse people
                            </Button>
                            <Button
                                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                onClick={() => setOpenMeetingDialog(true)}
                                sx={{ ...glassGhostButton(palette.rust), px: 2.2, py: .95, fontSize: '.72rem' }}
                            >
                                Schedule manually
                            </Button>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.35 }}>
                        {upcomingMeetings.length > 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, mb: .9 }}>
                                    <Box sx={{ ...glassPod(palette.rust, 28, '8px'), '& svg': { fontSize: 14 } }}>
                                        <ScheduleIcon />
                                    </Box>
                                    <Typography sx={{ fontSize: '.7rem', fontWeight: 880, color: palette.ink, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                                        Upcoming
                                    </Typography>
                                    <SectionRule color={palette.rust} width={36} />
                                    <Box sx={{ ...glassPill(palette.rust, true), ml: 'auto', px: 1, height: 20, fontSize: '.5rem' }}>
                                        {upcomingMeetings.length}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 1.15 }}>
                                    {upcomingMeetings.map((meeting) => (
                                        <MeetingCard
                                            key={meeting.id}
                                            meeting={meeting}
                                            onAction={handleMeetingAction}
                                            onJoin={handleJoin}
                                            processing={processingMeeting}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}

                        {pastMeetings.length > 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, mb: .9, mt: .6 }}>
                                    <Box sx={{ ...glassPod(palette.inkFaint, 28, '8px'), '& svg': { fontSize: 14 } }}>
                                        <HistoryIcon />
                                    </Box>
                                    <Typography sx={{ fontSize: '.7rem', fontWeight: 880, color: palette.inkLight, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                                        History
                                    </Typography>
                                    <SectionRule color={palette.inkFaint} width={36} />
                                    <Box sx={{ ...glassPill(palette.inkFaint), ml: 'auto', px: 1, height: 20, fontSize: '.5rem' }}>
                                        {pastMeetings.length}
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', xl: 'repeat(3, 1fr)' }, gap: 1 }}>
                                    {pastMeetings.map((meeting) => (
                                        <PastMeetingRow key={meeting.id} meeting={meeting} />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    // ============================================================================
    // RENDER PEOPLE TAB
    // ============================================================================

    const renderPeopleTab = () => (
        <>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 2.5, width: '100%', mb: 3 }}>
                {userStatsData.map((stat, index) => (
                    <PremiumStatCard key={stat.title} {...stat} delay={index * 0.06} />
                ))}
            </Box>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.5 }}>
                <Paper elevation={0} sx={{
                    ...glassCard({ level: 2, radius: '14px', accent: palette.teal }),
                    p: { xs: 2.5, md: 3 },
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                    transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                    '&:focus-within': { transform: 'translateY(-3px)', ...glassLift(3, palette.teal) },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0, left: '6%', right: '6%', height: '2px',
                        borderRadius: '0 0 999px 999px',
                        background: `linear-gradient(90deg, transparent, ${alpha(palette.teal, 0.6)}, ${alpha(palette.gold, 0.6)}, ${alpha(palette.pine, 0.6)}, ${alpha(palette.purple, 0.6)}, transparent)`,
                        backgroundSize: '300% 100%',
                        animation: 'tfEdgeShift 12s linear infinite',
                        pointerEvents: 'none',
                    },
                }}>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            placeholder="Search people by name, company, service, region…"
                            value={userSearchTerm}
                            onChange={handleUserSearch}
                            sx={{
                                flex: '1 1 260px',
                                minWidth: '200px',
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    fontSize: '0.85rem',
                                    transition: `all 0.3s ${EASE}`,
                                    '& fieldset': { border: 'none' },
                                    '&.Mui-focused': {
                                        borderColor: alpha(palette.teal, 0.5),
                                        boxShadow: `inset 0 1px 6px rgba(${SHADOW},0.06), 0 0 0 3px ${alpha(palette.teal, 0.14)}`,
                                    },
                                    '& input': { py: 1.3, color: palette.ink },
                                },
                            }}
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: userSearchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={clearUserSearch} aria-label="Clear search">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />

                        <FormControl size="small" sx={{ minWidth: '135px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Role</InputLabel>
                            <Select
                                value={userFilters.role}
                                onChange={(e) => handleUserFilterChange('role', e.target.value)}
                                label="Role"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Roles</MenuItem>
                                {userRoles.map(role => (
                                    <MenuItem key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: '145px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Specialization</InputLabel>
                            <Select
                                value={userFilters.specialization}
                                onChange={(e) => handleUserFilterChange('specialization', e.target.value)}
                                label="Specialization"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Specializations</MenuItem>
                                {allUserSpecializations.map(spec => (
                                    <MenuItem key={spec} value={spec}>{spec}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" sx={{ minWidth: '145px' }}>
                            <InputLabel sx={{ fontSize: '0.8rem' }}>Region</InputLabel>
                            <Select
                                value={userFilters.region}
                                onChange={(e) => handleUserFilterChange('region', e.target.value)}
                                label="Region"
                                sx={selectSx}
                                MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                            >
                                <MenuItem value="">All Regions</MenuItem>
                                {allUserRegions.map(region => (
                                    <MenuItem key={region} value={region}>{region}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <AnimatePresence>
                            {hasUserActiveFilter && (
                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }}>
                                    <Button
                                        size="small"
                                        startIcon={<ClearIcon />}
                                        onClick={clearUserFilters}
                                        sx={{ ...glassGhostButton(palette.teal), px: 1.75, py: 0.8, fontSize: '0.75rem' }}
                                    >
                                        Clear All
                                    </Button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.65rem' }}>
                                Sorted by AI Match
                            </Typography>
                            <Box sx={{ ...glassPill(palette.purple), px: 1.1, height: 22, fontSize: '0.6rem' }}>
                                <TrendingUpIcon sx={{ fontSize: 12 }} />High to Low
                            </Box>
                        </Box>
                    </Box>

                    {!loadingUsers && (
                        <Box sx={{ ...glassCard({ level: 0, radius: '12px' }), mt: 2, px: 1.5, py: 0.9, display: 'inline-flex' }}>
                            <Typography sx={{ color: palette.inkFaint, fontSize: '0.72rem', fontWeight: 700 }}>
                                Showing {filteredUsers.length} of {users.length} people
                                {pendingIntroCount > 0 && ` • ${pendingIntroCount} pending request${pendingIntroCount > 1 ? 's' : ''}`}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </motion.div>

            <Box sx={{ mt: 4 }}>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)', xl: 'repeat(4, 1fr)' },
                    gap: 3,
                    width: '100%',
                }}>
                    {loadingUsers
                        ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={`sk-${i}`} />)
                        : filteredUsers.map((user) => {
                            const targetId = user.uid || user.id;
                            const introStatus = getIntroStatusForUser(targetId);
                            const isLocked = introStatus.status === 'pending';
                            const isReceived = introStatus.direction === 'received';
                            const isAccepted = introStatus.status === 'accepted';

                            return (
                                <PeopleCard
                                    key={user.id}
                                    user={{ ...user, aiScore: userScores[user.id] || 0 }}
                                    onClick={() => handleViewUserDetails(user)}
                                    onIntroduce={handleIntroduceUser}
                                    onChat={handleOpenChatWithUser}
                                    onSchedule={handleScheduleMeeting}
                                    introStatus={introStatus.status}
                                    isLocked={isLocked}
                                    isRequestReceived={isReceived}
                                />
                            );
                        })}
                </Box>

                {!loadingUsers && filteredUsers.length === 0 && (
                    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                        <Box sx={{ ...glassCard({ level: 1, radius: '14px' }), textAlign: 'center', py: 7, px: 3, mt: 1 }}>
                            <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {[0, 1].map((i) => (
                                    <Box key={i} sx={{
                                        position: 'absolute', width: 72, height: 72, borderRadius: '50%',
                                        border: `1.5px solid ${palette.inkFaint}`, opacity: 0,
                                        animation: `tfSignalWave 3.2s ease-out ${i * 1.1}s infinite`,
                                    }} />
                                ))}
                                <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), opacity: 0.85 }}>
                                    <PersonIcon sx={{ fontSize: 34 }} />
                                </Box>
                            </Box>
                            <Typography variant="h6" sx={{ color: palette.ink, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.25rem' }}>
                                No people found
                            </Typography>
                            <Typography variant="body2" sx={{ color: palette.inkFaint, fontSize: '0.9rem', mt: 0.5 }}>
                                {hasUserActiveFilter ? 'Try adjusting your search or filters' : 'No other users are on the platform yet'}
                            </Typography>
                            {hasUserActiveFilter && (
                                <Button onClick={clearUserFilters} startIcon={<ClearIcon />} sx={{ ...glassGhostButton(palette.teal), mt: 2.5, px: 3, py: 1 }}>
                                    Clear filters
                                </Button>
                            )}
                        </Box>
                    </motion.div>
                )}
            </Box>
        </>
    );

    // ============================================================================
    // MAIN RENDER
    // ============================================================================

    return (
        <Box className="erp-page-shell erp-internal-page" sx={{ minHeight: '100vh', position: 'relative', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <Header />

            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    py: { xs: 1.25, md: 1.5 },
                    px: { xs: 1.25, sm: 2, md: 2.5, lg: 2.5 },
                    position: 'relative',
                    width: '100%',
                    maxWidth: '1780px',
                    mx: 'auto',
                    zIndex: 1,
                }}
            >
                {/* HEADER — same compact hierarchy as the attached reference */}
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.28 }}>
                    <Box className="erp-page-hero" sx={{ mb: 1.35 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexWrap: 'wrap', gap: 1.25 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .7, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontWeight: 780, fontSize: { xs: '1.35rem', md: '1.52rem' }, color: palette.ink, letterSpacing: '-.04em', lineHeight: 1.16 }}>
                                        Companies & Network
                                    </Typography>
                                    <Box sx={{ ...glassPill(palette.rust), height: 21, px: .8, fontSize: '0.54rem' }}>
                                        <LiveIcon sx={{ fontSize: 8, color: palette.success }} /> Live Directory
                                    </Box>
                                </Box>
                                <Typography sx={{ color: palette.inkLight, fontSize: '0.67rem', mt: .25 }}>
                                    Discover companies and people, manage invitations, and schedule meetings.
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .65, flexWrap: 'wrap' }}>
                                {pendingIntroCount > 0 && (
                                    <Box onClick={() => changeWorkspaceTab(1)} role="button" tabIndex={0} sx={{ ...glassPill(palette.rust), cursor: 'pointer' }}>
                                        <NotificationIcon sx={{ fontSize: 12 }} /> {pendingIntroCount} Intro{pendingIntroCount > 1 ? 's' : ''}
                                    </Box>
                                )}
                                {pendingReceived.length > 0 && (
                                    <Box onClick={() => changeWorkspaceTab(1)} role="button" tabIndex={0} sx={{ ...glassPill(palette.gold), cursor: 'pointer' }}>
                                        <InboxIcon sx={{ fontSize: 12 }} /> {pendingReceived.length} Request{pendingReceived.length > 1 ? 's' : ''}
                                    </Box>
                                )}
                                <Button
                                    variant="outlined"
                                    startIcon={<RefreshIcon sx={{ animation: loading ? 'tfRingSpin 1.1s linear infinite' : 'none' }} />}
                                    onClick={() => { fetchCompanies(); fetchUsers(); fetchIntroRequests(); }}
                                    disabled={loading || loadingUsers}
                                    sx={{ height: 32, px: 1.15, fontSize: '0.64rem' }}
                                >
                                    Refresh
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </motion.div>

                {/* STATS */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1, width: '100%', mb: 1.35 }}>
                    {statsData.map((stat, index) => (
                        <PremiumStatCard key={stat.title} {...stat} delay={index * 0.06} />
                    ))}
                </Box>

                {/* TABS */}
                <Paper elevation={0} sx={{
                    ...glassCard({ level: 2, radius: '14px', accent: palette.rust }),
                    width: '100%',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}>
                    <Box sx={{ borderBottom: `1px solid ${alpha(palette.ink, 0.065)}`, px: 1.25, pt: .65 }}>
                        <Tabs
                            value={activeTab}
                            onChange={(e, newValue) => changeWorkspaceTab(newValue)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    fontSize: '0.68rem',
                                    minHeight: 34,
                                    px: 1.35,
                                    cursor: 'pointer',
                                    '&:hover': { color: palette.rust },
                                },
                                '& .Mui-selected': { color: palette.rust },
                                '& .MuiTabs-indicator': { display: 'none' },
                            }}
                        >
                            <Tab
                                label="All Companies"
                                icon={<BusinessIcon sx={{ fontSize: 18 }} />}
                                iconPosition="start"
                            />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <InboxIcon sx={{ fontSize: 18 }} />
                                        Invitations
                                        {(pendingReceived.length + pendingIntroCount) > 0 && (
                                            <Box sx={{ ...glassPill(palette.gold, true), px: 0.8, height: 18, fontSize: '0.5rem' }}>
                                                {pendingReceived.length + pendingIntroCount}
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <ScheduleIcon sx={{ fontSize: 18 }} />
                                        Meetings
                                        {upcomingMeetings.length > 0 && (
                                            <Box sx={{ ...glassPill(palette.rust, true), px: 0.8, height: 18, fontSize: '0.5rem' }}>
                                                {upcomingMeetings.length}
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                            <Tab
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                        <PersonIcon sx={{ fontSize: 18 }} />
                                        People
                                        {users.length > 0 && (
                                            <Box sx={{ ...glassPill(palette.teal, true), px: 0.8, height: 18, fontSize: '0.5rem' }}>
                                                {users.length}
                                            </Box>
                                        )}
                                    </Box>
                                }
                            />
                        </Tabs>
                    </Box>

                    <Box sx={{ p: { xs: 1.1, md: 1.25 } }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -20 }}
                                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {activeTab === 0 && renderCompaniesTab()}
                                {activeTab === 1 && renderInvitationsTab()}
                                {activeTab === 2 && renderMeetingsTab()}
                                {activeTab === 3 && renderPeopleTab()}
                            </motion.div>
                        </AnimatePresence>
                    </Box>
                </Paper>
            </Container>

            {/* AI MESSAGE DIALOG (Companies) */}
            <Dialog
                open={openAIDialog}
                onClose={() => setOpenAIDialog(false)}
                maxWidth="md"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '26px', accent: palette.purple }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${alpha(palette.ink, 0.07)}`, pb: 2, px: 3 }}>
                    <Box sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, display: 'flex' }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={glassPod(palette.purple, 44, '14px')}>
                                <AiIcon sx={{ fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem', color: palette.ink }}>
                                    AI-Powered Connection
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                    Score: {Math.round(aiScore)}% • {aiScore >= 80 ? '🌟 Excellent Match' : aiScore >= 60 ? '💪 Strong Match' : '📊 Potential Match'}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setOpenAIDialog(false)} aria-label="Close" sx={{ ...glassWell('50%'), width: 34, height: 34, color: palette.inkFaint }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                            <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: palette.ink }}>
                                    Compatibility Score
                                </Typography>
                                <Typography fontWeight={700} sx={{
                                    color: aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : palette.rust,
                                    fontSize: '1.9rem', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", lineHeight: 1,
                                }}>
                                    {Math.round(aiScore)}%
                                </Typography>
                            </Box>
                            <Box sx={{ ...glassWell('999px'), p: '3px', mt: 1.5 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={aiScore}
                                    sx={{
                                        height: 6, borderRadius: 999, bgcolor: 'transparent',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 999,
                                            background: `linear-gradient(90deg, ${alpha(aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : palette.rust, 0.7)}, ${aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : palette.rust})`,
                                            boxShadow: `0 1px 10px ${alpha(aiScore >= 80 ? palette.pine : aiScore >= 60 ? palette.gold : palette.rust, 0.55)}`,
                                        },
                                    }}
                                />
                            </Box>
                        </Paper>

                        {aiDetails && aiDetails.length > 0 && (
                            <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: palette.inkFaint, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Why We Match
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                                    {aiDetails.slice(0, 5).map((detail, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, x: -12 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.06, duration: 0.35 }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ ...glassPod(palette.pine, 22, '50%') }}>
                                                    <CheckIcon sx={{ fontSize: 12 }} />
                                                </Box>
                                                <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                                                    {detail}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                            <Box sx={{ alignItems: 'center', gap: 2.5, display: 'flex' }}>
                                <Box sx={{ ...glassPod(palette.rust, 48, '15px'), fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.2rem', fontWeight: 700 }}>
                                    {selectedCompany?.companyName?.charAt(0) || 'C'}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.92rem', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                        {selectedCompany?.companyName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                        {selectedCompany?.country || 'No location'} • {selectedCompany?.companyType || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {aiSubject && (
                            <Box sx={{ ...glassWell('12px'), px: 1.75, py: 1.25 }}>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.4 }}>
                                    Subject
                                </Typography>
                                <Typography variant="body2" sx={{ color: palette.ink, fontSize: '0.82rem', fontWeight: 600 }}>
                                    {aiSubject}
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            value={aiMessage}
                            onChange={(e) => setAiMessage(e.target.value)}
                            aria-label="AI generated invitation message"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('14px'),
                                    fontSize: '0.85rem',
                                    color: palette.ink,
                                    transition: `all 0.3s ${EASE}`,
                                    '& fieldset': { border: 'none' },
                                    '&.Mui-focused': {
                                        borderColor: alpha(palette.purple, 0.5),
                                        boxShadow: `inset 0 1px 6px rgba(${SHADOW},0.06), 0 0 0 3px ${alpha(palette.purple, 0.14)}`,
                                    },
                                },
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Box
                                onClick={() => {
                                    navigator.clipboard.writeText(`https://telcofair.com/invite/${selectedCompany?.id}`);
                                    toast.success('Invitation link copied!');
                                }}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        navigator.clipboard.writeText(`https://telcofair.com/invite/${selectedCompany?.id}`);
                                        toast.success('Invitation link copied!');
                                    }
                                }}
                                sx={{
                                    ...glassPill(palette.teal), px: 1.3, height: 26, fontSize: '0.6rem', cursor: 'pointer',
                                    maxWidth: '100%', overflow: 'hidden',
                                    '&:hover': { background: alpha(palette.teal, 0.18) },
                                    '&:focus-visible': { outline: `2px solid ${palette.teal}`, outlineOffset: 2 },
                                }}
                            >
                                <LinkIcon sx={{ fontSize: 12 }} />
                                <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {`telcofair.com/invite/${selectedCompany?.id || ''}`}
                                </Box>
                            </Box>
                            <Box sx={{ ...glassPill(palette.purple), px: 1.3, height: 26, fontSize: '0.6rem' }}>
                                <AiIcon sx={{ fontSize: 12 }} />Score: {Math.round(aiScore)}%
                            </Box>
                            <Box sx={{ ...glassPill(palette.rust), px: 1.3, height: 26, fontSize: '0.6rem' }}>
                                <BusinessIcon sx={{ fontSize: 12 }} />{selectedCompany?.companyType || 'N/A'}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(palette.ink, 0.07)}`, gap: 1.5 }}>
                    <Button onClick={() => setOpenAIDialog(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendAIMessage}
                        disabled={processingInvite || inviteSent}
                        startIcon={processingInvite ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ ...glassButton(palette.purple, palette.navy), px: 3, py: 1.1, fontSize: '0.82rem' }}
                    >
                        {inviteSent ? 'Sent ✓' : processingInvite ? 'Sending…' : 'Send AI Invite'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* COMPANY DETAIL DIALOG */}
            <Dialog
                open={openCompanyDetailDialog}
                onClose={() => setOpenCompanyDetailDialog(false)}
                maxWidth="lg"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', backgroundColor: 'rgba(15,29,43,.42)' } }, ...({ paper: {
                    sx: {
                        width: 'min(920px, calc(100vw - 24px))',
                        maxWidth: '920px',
                        borderRadius: '22px',
                        overflow: 'hidden',
                        bgcolor: '#F3F8FB',
                        color: '#182235',
                        border: '1px solid rgba(31,62,82,.10)',
                        boxShadow: '0 28px 90px rgba(12,35,52,.24), 0 8px 24px rgba(12,35,52,.10)',
                        backgroundImage: 'radial-gradient(circle at 96% 5%, rgba(var(--primary-rgb),.065), transparent 28%), radial-gradient(circle at 4% 96%, rgba(var(--secondary-rgb),.045), transparent 24%)',
                    },
                } }) }}
            >
                {selectedCompanyDetail && (() => {
                    const cover = getCompanyCoverUrl(selectedCompanyDetail);
                    const logo = getCompanyLogoUrl(selectedCompanyDetail);
                    const inv = invitations[selectedCompanyDetail.id];
                    const isConnected = ['accepted', 'connected'].includes(inv?.status);
                    const isPending = inv?.status === 'pending';
                    const detailTiles = [
                        { label: 'Company Type', value: asText(selectedCompanyDetail.companyType) || 'Company', color: palette.rust, icon: <BusinessIcon sx={{ fontSize: 17 }} /> },
                        { label: 'Primary Service', value: asText(selectedCompanyDetail.primaryService) || 'Not specified', color: palette.pine, icon: <StarsIcon sx={{ fontSize: 17 }} /> },
                        { label: 'Primary Region', value: asText(selectedCompanyDetail.primaryRegion) || selectedCompanyDetail.country || 'Not specified', color: palette.teal, icon: <PublicIcon sx={{ fontSize: 17 }} /> },
                        { label: 'Looking For', value: asText(selectedCompanyDetail.primaryConnectWith) || 'Not specified', color: palette.gold, icon: <HandshakeIcon sx={{ fontSize: 17 }} /> },
                    ];
                    const detailSections = [
                        {
                            title: 'Services & Expertise', color: palette.gold, icon: StarsIcon,
                            items: [
                                ...asList(selectedCompanyDetail.primaryService),
                                ...asList(selectedCompanyDetail.secondaryService),
                            ],
                        },
                        {
                            title: 'Business Regions', color: palette.teal, icon: PublicIcon,
                            items: [
                                ...asList(selectedCompanyDetail.primaryRegion),
                                ...asList(selectedCompanyDetail.secondaryRegion),
                            ],
                        },
                        {
                            title: 'Partnership Interests', color: palette.purple, icon: HandshakeIcon,
                            items: [
                                ...asList(selectedCompanyDetail.primaryConnectWith),
                                ...asList(selectedCompanyDetail.secondaryConnectWith),
                                ...(selectedCompanyDetail.otherConnectWith ? [selectedCompanyDetail.otherConnectWith] : []),
                            ],
                        },
                    ].filter((section) => section.items.length > 0);

                    return (
                        <>
                            <Box sx={{
                                minHeight: { xs: 180, sm: 205 },
                                position: 'relative',
                                display: 'flex',
                                alignItems: 'flex-end',
                                overflow: 'hidden',
                                background: cover
                                    ? `url(${cover}) center/cover no-repeat`
                                    : `linear-gradient(118deg, ${palette.rustDeep}, ${palette.rust} 48%, ${palette.pine})`,
                                '&::before': {
                                    content: '""', position: 'absolute', inset: 0,
                                    background: cover
                                        ? 'linear-gradient(180deg, rgba(8,24,38,.18) 10%, rgba(8,24,38,.80) 100%)'
                                        : 'radial-gradient(circle at 88% 10%, rgba(255,255,255,.18), transparent 25%), linear-gradient(180deg, rgba(8,24,38,.03), rgba(8,24,38,.20))',
                                },
                                '&::after': {
                                    content: '""', position: 'absolute', width: 190, height: 190, borderRadius: '50%',
                                    right: -58, top: -90, background: 'rgba(255,255,255,.08)',
                                    boxShadow: '-170px 215px 0 24px rgba(255,255,255,.045)', pointerEvents: 'none',
                                },
                            }}>
                                <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 3, display: 'flex', gap: .8 }}>
                                    <Tooltip title="Associated Users">
                                        <IconButton
                                            onClick={() => { setOpenCompanyDetailDialog(false); handleViewCompanyUsers(selectedCompanyDetail); }}
                                            sx={{ width: 36, height: 36, color: '#fff', bgcolor: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.24)', backdropFilter: 'blur(10px)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                                        >
                                            <PeopleAltIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <IconButton
                                        onClick={() => setOpenCompanyDetailDialog(false)}
                                        sx={{ width: 36, height: 36, color: '#fff', bgcolor: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.24)', backdropFilter: 'blur(10px)', '&:hover': { bgcolor: 'rgba(255,255,255,.24)' } }}
                                    >
                                        <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Box>

                                <Box sx={{ position: 'relative', zIndex: 2, width: '100%', p: { xs: 1.7, sm: 2.2 }, display: 'flex', alignItems: 'flex-end', gap: 1.5 }}>
                                    <Avatar sx={{
                                        width: { xs: 62, sm: 72 }, height: { xs: 62, sm: 72 }, borderRadius: '16px',
                                        bgcolor: '#fff', color: palette.rust, border: '3px solid rgba(255,255,255,.92)',
                                        boxShadow: '0 12px 30px rgba(6,25,39,.28)', fontWeight: 900, fontSize: '1.55rem', overflow: 'hidden', flex: '0 0 auto',
                                    }}>
                                        {logo ? <Box component="img" src={logo} alt={selectedCompanyDetail.companyName || 'Company'} sx={{ width: '100%', height: '100%', objectFit: 'contain', p: .6, bgcolor: '#fff' }} /> : (selectedCompanyDetail.companyName?.charAt(0) || 'C')}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .7, flexWrap: 'wrap', mb: .35 }}>
                                            <Typography sx={{ color: '#fff', fontSize: { xs: '1.2rem', sm: '1.55rem' }, fontWeight: 900, letterSpacing: '-.03em', lineHeight: 1.08, textShadow: '0 2px 12px rgba(0,0,0,.24)' }}>
                                                {selectedCompanyDetail.companyName}
                                            </Typography>
                                            {selectedCompanyDetail.isVerified && (
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .4, px: .8, height: 22, borderRadius: '999px', bgcolor: 'rgba(30,182,124,.88)', color: '#fff', border: '1px solid rgba(255,255,255,.24)', fontSize: '.58rem', fontWeight: 900 }}>
                                                    <VerifiedIcon sx={{ fontSize: 12 }} /> Verified
                                                </Box>
                                            )}
                                        </Box>
                                        <Typography sx={{ color: 'rgba(255,255,255,.86)', fontSize: '.75rem', fontWeight: 650 }}>
                                            {[selectedCompanyDetail.city, selectedCompanyDetail.country, asText(selectedCompanyDetail.companyType)].filter(Boolean).join(' • ') || 'Company profile'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: .6, mt: .8, flexWrap: 'wrap' }}>
                                            <Box sx={{ px: .85, height: 22, display: 'inline-flex', alignItems: 'center', borderRadius: '999px', color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.20)', fontSize: '.56rem', fontWeight: 850 }}>
                                                {isConnected ? 'Connected' : isPending ? 'Introduction Pending' : 'Discover'}
                                            </Box>
                                            <Box sx={{ px: .85, height: 22, display: 'inline-flex', alignItems: 'center', borderRadius: '999px', color: '#fff', bgcolor: 'rgba(255,255,255,.13)', border: '1px solid rgba(255,255,255,.20)', fontSize: '.56rem', fontWeight: 850 }}>
                                                {selectedCompanyDetail.isActive === false ? 'Inactive' : 'Active'}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            <DialogContent sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'transparent' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, flexWrap: 'wrap', mb: 1.4 }}>
                                    {!isConnected && !isPending && (
                                        <Button
                                            startIcon={<AiIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => { setOpenCompanyDetailDialog(false); handleAIConnect(selectedCompanyDetail); }}
                                            sx={{ ...glassButton(palette.purple, palette.navy), color: '#fff !important', minHeight: 34, px: 1.5, fontSize: '.68rem' }}
                                        >AI Intro</Button>
                                    )}
                                    <Button
                                        startIcon={isConnected ? <ChatIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 15 }} />}
                                        disabled={!isConnected}
                                        onClick={() => { setOpenCompanyDetailDialog(false); handleOpenChat(selectedCompanyDetail); }}
                                        sx={{ ...glassGhostButton(isConnected ? palette.gold : palette.inkFaint), minHeight: 34, px: 1.4, fontSize: '.68rem', color: '#26364A !important' }}
                                    >Chat</Button>
                                    <Button
                                        startIcon={isConnected ? <EventIcon sx={{ fontSize: 16 }} /> : <LockIcon sx={{ fontSize: 15 }} />}
                                        disabled={!isConnected}
                                        onClick={() => { setOpenCompanyDetailDialog(false); handleScheduleMeeting(selectedCompanyDetail); }}
                                        sx={{ ...glassButton(isConnected ? palette.rust : palette.inkFaint, isConnected ? palette.rustDeep : palette.inkFaint), minHeight: 34, px: 1.5, fontSize: '.68rem' }}
                                    >Schedule</Button>
                                    <EntityRatingButton targetType="company" targetId={selectedCompanyDetail.id || selectedCompanyDetail.companyId} targetName={selectedCompanyDetail.companyName || 'Company'} target={selectedCompanyDetail} color="#E9A220" />
                                    <Button
                                        startIcon={<PeopleAltIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => { setOpenCompanyDetailDialog(false); handleViewCompanyUsers(selectedCompanyDetail); }}
                                        sx={{ ...glassGhostButton(palette.teal), minHeight: 34, px: 1.4, fontSize: '.68rem', color: '#26364A !important' }}
                                    >People</Button>
                                    <Button
                                        startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => { setOpenCompanyDetailDialog(false); navigate(`/dashboard/company/${selectedCompanyDetail.id}`); }}
                                        sx={{ ...glassGhostButton(palette.rust), minHeight: 34, px: 1.4, fontSize: '.68rem', color: '#26364A !important' }}
                                    >Full Profile</Button>
                                </Box>

                                {isPending && (
                                    <Box sx={{ mb: 1.4, p: 1.15, borderRadius: '13px', bgcolor: 'rgba(244,170,45,.09)', border: '1px solid rgba(244,170,45,.20)', display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box sx={{ ...glassPod(palette.gold, 30, '9px') }}><LockIcon sx={{ fontSize: 15 }} /></Box>
                                        <Typography sx={{ color: '#526174', fontSize: '.71rem', lineHeight: 1.45 }}>
                                            {inv?.isSender ? 'Your AI introduction is waiting for this company to respond.' : 'This company sent you an introduction. Review it from Invitations to unlock communication.'}
                                        </Typography>
                                    </Box>
                                )}

                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1, mb: 1.25 }}>
                                    {detailTiles.map((tile) => (
                                        <Box key={tile.label} sx={{ position: 'relative', overflow: 'hidden', p: 1.2, minHeight: 92, borderRadius: '15px', bgcolor: '#fff', border: '1px solid rgba(31,62,82,.08)', boxShadow: '0 9px 22px rgba(25,57,78,.065), inset 0 1px 0 rgba(255,255,255,.95)' }}>
                                            <Box sx={{ position: 'absolute', width: 78, height: 78, borderRadius: '50%', right: -34, bottom: -40, bgcolor: alpha(tile.color, .06) }} />
                                            <Box sx={{ ...glassPod(tile.color, 31, '9px'), mb: .75 }}>{tile.icon}</Box>
                                            <Typography sx={{ color: '#718197', fontSize: '.52rem', fontWeight: 900, letterSpacing: '.07em', textTransform: 'uppercase' }}>{tile.label}</Typography>
                                            <Typography sx={{ mt: .25, color: '#1B293C', fontSize: '.72rem', lineHeight: 1.28, fontWeight: 850, wordBreak: 'break-word' }}>{tile.value}</Typography>
                                        </Box>
                                    ))}
                                </Box>

                                <Box sx={{ p: 1.45, mb: 1.15, borderRadius: '15px', bgcolor: '#fff', border: '1px solid rgba(31,62,82,.08)', boxShadow: '0 9px 22px rgba(25,57,78,.06)' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mb: .65 }}>
                                        <Box sx={{ ...glassPod(palette.rust, 28, '8px') }}><InfoIcon sx={{ fontSize: 14 }} /></Box>
                                        <Typography sx={{ color: '#1B293C', fontSize: '.72rem', fontWeight: 900 }}>About the company</Typography>
                                    </Box>
                                    <Typography sx={{ color: '#5B6A7D', fontSize: '.74rem', lineHeight: 1.58 }}>
                                        {selectedCompanyDetail.description || 'No company description has been provided yet.'}
                                    </Typography>
                                </Box>

                                {detailSections.length > 0 && (
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: detailSections.length === 1 ? '1fr' : 'repeat(2, minmax(0,1fr))' }, gap: 1 }}>
                                        {detailSections.map((section, sectionIndex) => {
                                            const SectionIcon = section.icon;
                                            return (
                                                <Box key={section.title} sx={{ position: 'relative', overflow: 'hidden', p: 1.3, borderRadius: '15px', bgcolor: '#fff', border: '1px solid rgba(31,62,82,.08)', boxShadow: '0 9px 22px rgba(25,57,78,.055)', gridColumn: { md: detailSections.length === 3 && sectionIndex === 2 ? '1 / -1' : 'auto' } }}>
                                                    <Box sx={{ position: 'absolute', right: -12, bottom: -22, opacity: .045, color: section.color }}><SectionIcon sx={{ fontSize: 90 }} /></Box>
                                                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: .7, mb: .8 }}>
                                                            <Box sx={{ ...glassPod(section.color, 28, '8px') }}><SectionIcon sx={{ fontSize: 14 }} /></Box>
                                                            <Typography sx={{ color: '#1B293C', fontSize: '.68rem', fontWeight: 900 }}>{section.title}</Typography>
                                                        </Box>
                                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: .55 }}>
                                                            {section.items.map((item, idx) => (
                                                                <Box key={`${section.title}-${idx}`} sx={{ px: .8, py: .45, borderRadius: '999px', bgcolor: alpha(section.color, .08), color: '#33465A', border: `1px solid ${alpha(section.color, .14)}`, fontSize: '.59rem', fontWeight: 800 }}>
                                                                    {item}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </DialogContent>

                            <DialogActions sx={{ px: 2, py: 1.15, bgcolor: 'rgba(255,255,255,.88)', borderTop: '1px solid rgba(31,62,82,.08)' }}>
                                <Button onClick={() => setOpenCompanyDetailDialog(false)} sx={{ color: '#33465A !important', fontWeight: 850, fontSize: '.69rem' }}>Close</Button>
                            </DialogActions>
                        </>
                    );
                })()}
            </Dialog>


            {/* COMPANY USERS DIALOG */}
            <Dialog
                open={openCompanyUsersDialog}
                onClose={() => setOpenCompanyUsersDialog(false)}
                maxWidth="md"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '26px', accent: palette.teal }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${alpha(palette.ink, 0.07)}`, pb: 2, px: 3 }}>
                    <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={glassPod(palette.teal, 44, '14px')}>
                                <PeopleAltIcon sx={{ fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem', color: palette.ink }}>
                                    Team Members
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                    {selectedCompanyForUsers?.companyName || 'Company'} • {companyUsers.length} members
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setOpenCompanyUsersDialog(false)} aria-label="Close" sx={{ ...glassWell('50%'), width: 34, height: 34, color: palette.inkFaint }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, px: 3 }}>
                    {loadingCompanyUsers ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress sx={{ color: palette.teal }} />
                        </Box>
                    ) : companyUsers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Box sx={{ ...glassPod(palette.inkFaint, 64, '50%'), mx: 'auto', mb: 2, opacity: 0.7 }}>
                                <PeopleAltIcon sx={{ fontSize: 30 }} />
                            </Box>
                            <Typography sx={{ color: palette.inkFaint, fontSize: '0.9rem' }}>
                                No team members found
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {companyUsers.map((user) => {
                                const displayName = user.displayName || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User';
                                const initials = displayName.charAt(0).toUpperCase();
                                const hasProfileImage = Boolean(user.profileImage);
                                const specializations = asList(user.specialization);
                                const primaryServices = asList(user.primaryServiceProvided);
                                const regions = asList(user.primaryRegionAvailability);
                                const isCurrentUser = user.uid === currentUser?.uid;

                                return (
                                    <motion.div
                                        key={user.id}
                                        initial={{ opacity: 0, y: 16 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.35 }}
                                    >
                                        <Paper elevation={0} sx={{
                                            ...glassCard({ level: 1, radius: '18px', accent: palette.teal }),
                                            p: 2.5,
                                            transition: `transform 0.3s ${EASE}, box-shadow 0.3s ease`,
                                            '&:hover': { transform: 'translateY(-3px)', ...glassLift(3, palette.teal) },
                                        }}>
                                            <Box sx={{ alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, display: 'flex' }}>
                                                <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                                                    <Avatar
                                                        sx={{
                                                            width: 48,
                                                            height: 48,
                                                            borderRadius: '14px',
                                                            background: hasProfileImage ? alpha(palette.paper, 0.7) : `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`,
                                                            backdropFilter: 'blur(10px) saturate(180%)',
                                                            WebkitBackdropFilter: 'blur(10px) saturate(180%)',
                                                            border: '1px solid rgba(255,255,255,0.5)',
                                                            boxShadow: `0 10px 24px ${alpha(palette.teal, 0.28)}`,
                                                            flexShrink: 0,
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                                            fontSize: '1.2rem',
                                                        }}
                                                    >
                                                        {hasProfileImage ? (
                                                            <img
                                                                src={user.profileImage}
                                                                alt={displayName}
                                                                loading="lazy"
                                                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '14px' }}
                                                                onError={(e) => { e.target.style.display = 'none'; }}
                                                            />
                                                        ) : initials}
                                                    </Avatar>
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                                            <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.92rem' }}>
                                                                {displayName}
                                                            </Typography>
                                                            {isCurrentUser && (
                                                                <Chip
                                                                    label="You"
                                                                    size="small"
                                                                    sx={{
                                                                        ...glassPill(palette.gold, true),
                                                                        height: 18,
                                                                        fontSize: '0.5rem',
                                                                    }}
                                                                />
                                                            )}
                                                            {user.isVerified && (
                                                                <Tooltip title="Verified User">
                                                                    <VerifiedIcon sx={{ fontSize: 14, color: palette.pine }} />
                                                                </Tooltip>
                                                            )}
                                                        </Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
                                                            <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                                                <WorkIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {user.role || 'Member'}
                                                            </Typography>
                                                            {user.businessTitle && (
                                                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                                                    <TitleIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {user.businessTitle}
                                                                </Typography>
                                                            )}
                                                            {user.country && (
                                                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                                                    <LocationIcon sx={{ fontSize: 12, verticalAlign: 'middle' }} /> {user.country}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                {!isCurrentUser && (
                                                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                                                        <Tooltip title="Send Introduction">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setOpenCompanyUsersDialog(false); handleSendIntroToCompanyUser(user); }}
                                                                sx={{ ...glassPod(palette.purple, 32, '10px'), '&:hover': { transform: 'scale(1.1)' } }}
                                                            >
                                                                <SendIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Chat">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setOpenCompanyUsersDialog(false); handleOpenChatWithUser(user); }}
                                                                sx={{ ...glassPod(palette.gold, 32, '10px'), '&:hover': { transform: 'scale(1.1)' } }}
                                                            >
                                                                <ChatIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Schedule Meeting">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setOpenCompanyUsersDialog(false); handleScheduleMeeting(user); }}
                                                                sx={{ ...glassPod(palette.rust, 32, '10px'), '&:hover': { transform: 'scale(1.1)' } }}
                                                            >
                                                                <EventIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Profile">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => { setOpenCompanyUsersDialog(false); handleViewUserDetails(user); }}
                                                                sx={{ ...glassPod(palette.teal, 32, '10px'), '&:hover': { transform: 'scale(1.1)' } }}
                                                            >
                                                                <VisibilityIcon sx={{ fontSize: 15 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 1.5 }}>
                                                {specializations.slice(0, 3).map((spec, i) => (
                                                    <Box key={`spec-${i}`} sx={{ ...glassPill(palette.purple), px: 1, height: 20, fontSize: '0.5rem' }}>
                                                        <StarsIcon sx={{ fontSize: 10 }} />{spec}
                                                    </Box>
                                                ))}
                                                {primaryServices.slice(0, 2).map((service, i) => (
                                                    <Box key={`service-${i}`} sx={{ ...glassPill(palette.gold), px: 1, height: 20, fontSize: '0.5rem' }}>
                                                        <DiamondIcon sx={{ fontSize: 10 }} />{service}
                                                    </Box>
                                                ))}
                                                {regions.slice(0, 2).map((region, i) => (
                                                    <Box key={`region-${i}`} sx={{ ...glassPill(palette.teal), px: 1, height: 20, fontSize: '0.5rem' }}>
                                                        <PublicIcon sx={{ fontSize: 10 }} />{region}
                                                    </Box>
                                                ))}
                                            </Box>

                                            {(user.email || user.phone || user.linkedinProfile) && (
                                                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                                                    {user.email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkFaint }}>
                                                            <EmailIcon sx={{ fontSize: 12 }} />
                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{user.email}</Typography>
                                                        </Box>
                                                    )}
                                                    {user.phone && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkFaint }}>
                                                            <PhoneIcon sx={{ fontSize: 12 }} />
                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>{user.phone}</Typography>
                                                        </Box>
                                                    )}
                                                    {user.linkedinProfile && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: palette.inkFaint }}>
                                                            <LinkedInIcon sx={{ fontSize: 12, color: palette.linkedin }} />
                                                            <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                                                <a href={safeExternalUrl(user.linkedinProfile, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                                    LinkedIn
                                                                </a>
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            )}
                                        </Paper>
                                    </motion.div>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(palette.ink, 0.07)}` }}>
                    <Button onClick={() => setOpenCompanyUsersDialog(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* SCHEDULE MEETING DIALOG */}
            <Dialog
                open={openMeetingDialog}
                onClose={() => setOpenMeetingDialog(false)}
                maxWidth="sm"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '26px', accent: palette.rust }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${alpha(palette.ink, 0.07)}`, pb: 2, px: 3 }}>
                    <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={glassPod(palette.rust, 44, '14px')}>
                                <EventIcon sx={{ fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem', color: palette.ink }}>
                                    Schedule Meeting
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                    {selectedMeetingCompany?.companyName || selectedMeetingCompany?.displayName || 'Select a company'}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setOpenMeetingDialog(false)} aria-label="Close" sx={{ ...glassWell('50%'), width: 34, height: 34, color: palette.inkFaint }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            fullWidth
                            label="Meeting Title *"
                            value={meetingForm.title}
                            onChange={(e) => handleMeetingFormChange('title', e.target.value)}
                            placeholder="e.g., Partnership Discussion"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    '& fieldset': { border: 'none' },
                                }
                            }}
                        />

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    type="date"
                                    label="Date *"
                                    value={meetingForm.date}
                                    onChange={(e) => handleMeetingFormChange('date', e.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    type="time"
                                    label="Time *"
                                    value={meetingForm.time}
                                    onChange={(e) => handleMeetingFormChange('time', e.target.value)}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Duration</InputLabel>
                                    <Select
                                        value={meetingForm.duration}
                                        onChange={(e) => handleMeetingFormChange('duration', e.target.value)}
                                        label="Duration"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }}
                                    >
                                        <MenuItem value="15">15 min</MenuItem>
                                        <MenuItem value="30">30 min</MenuItem>
                                        <MenuItem value="45">45 min</MenuItem>
                                        <MenuItem value="60">60 min</MenuItem>
                                        <MenuItem value="90">90 min</MenuItem>
                                        <MenuItem value="120">120 min</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Meeting Type</InputLabel>
                                    <Select
                                        value={meetingForm.type}
                                        onChange={(e) => handleMeetingFormChange('type', e.target.value)}
                                        label="Meeting Type"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                        }}
                                    >
                                        <MenuItem value="virtual">Virtual</MenuItem>
                                        <MenuItem value="in-person">In-Person</MenuItem>
                                        <MenuItem value="hybrid">Hybrid</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {meetingForm.type === 'in-person' && (
                            <TextField
                                fullWidth
                                label="Location"
                                value={meetingForm.location}
                                onChange={(e) => handleMeetingFormChange('location', e.target.value)}
                                placeholder="Address or venue"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        ...glassWell('12px'),
                                        '& fieldset': { border: 'none' },
                                    }
                                }}
                            />
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Agenda"
                            value={meetingForm.agenda}
                            onChange={(e) => handleMeetingFormChange('agenda', e.target.value)}
                            placeholder="What will be discussed?"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    '& fieldset': { border: 'none' },
                                }
                            }}
                        />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(palette.ink, 0.07)}`, gap: 1.5 }}>
                    <Button onClick={() => setOpenMeetingDialog(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateMeeting}
                        disabled={processingMeeting}
                        startIcon={processingMeeting ? <CircularProgress size={16} color="inherit" /> : <EventIcon />}
                        sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 3, py: 1.1, fontSize: '0.82rem' }}
                    >
                        {processingMeeting ? 'Scheduling...' : 'Schedule Meeting'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* USER INTRODUCTION DIALOG */}
            <Dialog
                open={openUserIntroDialog}
                onClose={() => setOpenUserIntroDialog(false)}
                maxWidth="md"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '26px', accent: palette.teal }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{ borderBottom: `1px solid ${alpha(palette.ink, 0.07)}`, pb: 2, px: 3 }}>
                    <Box sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2, display: 'flex' }}>
                        <Box sx={{ alignItems: 'center', gap: 2, display: 'flex' }}>
                            <Box sx={glassPod(palette.teal, 44, '14px')}>
                                <AiIcon sx={{ fontSize: 22 }} />
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.1rem', color: palette.ink }}>
                                    AI-Powered Introduction
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                    Score: {Math.round(userIntroScore)}% • {userIntroScore >= 80 ? '🌟 Excellent Match' : userIntroScore >= 60 ? '💪 Strong Match' : '📊 Potential Match'}
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={() => setOpenUserIntroDialog(false)} aria-label="Close" sx={{ ...glassWell('50%'), width: 34, height: 34, color: palette.inkFaint }}>
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                            <Box sx={{ alignItems: 'center', justifyContent: 'space-between', display: 'flex' }}>
                                <Typography variant="body2" fontWeight={700} sx={{ fontSize: '0.82rem', color: palette.ink }}>
                                    Compatibility Score
                                </Typography>
                                <Typography fontWeight={700} sx={{
                                    color: userIntroScore >= 80 ? palette.pine : userIntroScore >= 60 ? palette.gold : palette.rust,
                                    fontSize: '1.9rem', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", lineHeight: 1,
                                }}>
                                    {Math.round(userIntroScore)}%
                                </Typography>
                            </Box>
                            <Box sx={{ ...glassWell('999px'), p: '3px', mt: 1.5 }}>
                                <LinearProgress
                                    variant="determinate"
                                    value={userIntroScore}
                                    sx={{
                                        height: 6, borderRadius: 999, bgcolor: 'transparent',
                                        '& .MuiLinearProgress-bar': {
                                            borderRadius: 999,
                                            background: `linear-gradient(90deg, ${alpha(userIntroScore >= 80 ? palette.pine : userIntroScore >= 60 ? palette.gold : palette.rust, 0.7)}, ${userIntroScore >= 80 ? palette.pine : userIntroScore >= 60 ? palette.gold : palette.rust})`,
                                            boxShadow: `0 1px 10px ${alpha(userIntroScore >= 80 ? palette.pine : userIntroScore >= 60 ? palette.gold : palette.rust, 0.55)}`,
                                        },
                                    }}
                                />
                            </Box>
                        </Paper>

                        {userIntroDetails && userIntroDetails.length > 0 && (
                            <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: palette.inkFaint, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                    Why We Match
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                                    {userIntroDetails.slice(0, 5).map((detail, idx) => (
                                        <motion.div
                                            key={idx}
                                            initial={{ opacity: 0, x: -12 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: idx * 0.06, duration: 0.35 }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                <Box sx={{ ...glassPod(palette.pine, 22, '50%') }}>
                                                    <CheckIcon sx={{ fontSize: 12 }} />
                                                </Box>
                                                <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                                                    {detail}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    ))}
                                </Box>
                            </Paper>
                        )}

                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                            <Box sx={{ alignItems: 'center', gap: 2.5, display: 'flex' }}>
                                <Box sx={{ ...glassPod(palette.teal, 48, '15px'), fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontSize: '1.2rem', fontWeight: 700 }}>
                                    {selectedUser?.displayName?.charAt(0) || selectedUser?.firstName?.charAt(0) || 'U'}
                                </Box>
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.92rem', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                                        {selectedUser?.displayName || `${selectedUser?.firstName || ''} ${selectedUser?.lastName || ''}`.trim() || 'User'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                        {selectedUser?.companyName || 'No company'} • {selectedUser?.role || 'Member'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        {userIntroSubject && (
                            <Box sx={{ ...glassWell('12px'), px: 1.75, py: 1.25 }}>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', mb: 0.4 }}>
                                    Subject
                                </Typography>
                                <Typography variant="body2" sx={{ color: palette.ink, fontSize: '0.82rem', fontWeight: 600 }}>
                                    {userIntroSubject}
                                </Typography>
                            </Box>
                        )}

                        <TextField
                            fullWidth
                            multiline
                            rows={8}
                            value={userIntroMessage}
                            onChange={(e) => setUserIntroMessage(e.target.value)}
                            aria-label="AI generated introduction message"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('14px'),
                                    fontSize: '0.85rem',
                                    color: palette.ink,
                                    transition: `all 0.3s ${EASE}`,
                                    '& fieldset': { border: 'none' },
                                    '&.Mui-focused': {
                                        borderColor: alpha(palette.teal, 0.5),
                                        boxShadow: `inset 0 1px 6px rgba(${SHADOW},0.06), 0 0 0 3px ${alpha(palette.teal, 0.14)}`,
                                    },
                                },
                            }}
                        />

                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ ...glassPill(palette.teal), px: 1.3, height: 26, fontSize: '0.6rem' }}>
                                <AiIcon sx={{ fontSize: 12 }} />Score: {Math.round(userIntroScore)}%
                            </Box>
                            <Box sx={{ ...glassPill(palette.purple), px: 1.3, height: 26, fontSize: '0.6rem' }}>
                                <StarsIcon sx={{ fontSize: 12 }} />{asText(selectedUser?.specialization) || 'N/A'}
                            </Box>
                            <Box sx={{ ...glassPill(palette.gold), px: 1.3, height: 26, fontSize: '0.6rem' }}>
                                <DiamondIcon sx={{ fontSize: 12 }} />{asText(selectedUser?.primaryServiceProvided) || 'N/A'}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(palette.ink, 0.07)}`, gap: 1.5 }}>
                    <Button onClick={() => setOpenUserIntroDialog(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSendUserIntro}
                        disabled={processingInvite}
                        startIcon={processingInvite ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                        sx={{ ...glassButton(palette.teal, palette.tealDeep), px: 3, py: 1.1, fontSize: '0.82rem' }}
                    >
                        {processingInvite ? 'Sending...' : 'Send Introduction'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* USER DETAIL DIALOG */}
            <Dialog
                open={openUserDetailDialog}
                onClose={() => setOpenUserDetailDialog(false)}
                maxWidth="md"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '26px', accent: palette.teal }), overflow: 'hidden', maxWidth: '760px' } } }) }}
            >
                {selectedUserDetail && (
                    <>
                        <Box sx={{
                            height: 140,
                            width: '100%',
                            position: 'relative',
                            background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`,
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0, left: 0, right: 0, height: '55%',
                                background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.3))',
                            },
                        }}>
                            <Box sx={{ position: 'absolute', bottom: -42, left: 24, zIndex: 2 }}>
                                <Avatar sx={{
                                    width: 84,
                                    height: 84,
                                    borderRadius: '14px',
                                    background: `linear-gradient(135deg, ${palette.teal}, ${palette.tealDeep})`,
                                    boxShadow: `0 14px 34px ${alpha(palette.teal, 0.34)}, inset 0 1px 0 rgba(255,255,255,0.5)`,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                    fontSize: '2rem',
                                    border: `4px solid ${alpha(palette.paper, 0.9)}`,
                                }}>
                                    {selectedUserDetail.profileImage ? (
                                        <img src={selectedUserDetail.profileImage} alt={selectedUserDetail.displayName || 'User'} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '18px' }} />
                                    ) : (selectedUserDetail.displayName?.charAt(0) || selectedUserDetail.firstName?.charAt(0) || 'U')}
                                </Avatar>
                            </Box>

                            <IconButton
                                onClick={() => setOpenUserDetailDialog(false)}
                                aria-label="Close"
                                sx={{
                                    position: 'absolute', top: 12, right: 12, zIndex: 2,
                                    width: 38, height: 38,
                                    color: '#fff',
                                    background: 'rgba(255,255,255,0.18)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    '&:hover': { background: 'rgba(255,255,255,0.3)' },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 19 }} />
                            </IconButton>

                            {selectedUserDetail.isVerified && (
                                <Box sx={{ position: 'absolute', bottom: 14, right: 16, zIndex: 2 }}>
                                    <Box sx={{ ...glassPill(palette.pine, true), px: 1.3, height: 26, fontSize: '0.65rem' }}>
                                        <VerifiedIcon sx={{ fontSize: 14 }} />Verified
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <DialogContent sx={{ pt: 6.5, pb: 2, px: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9, flexWrap: 'wrap' }}>
                                        <Typography variant="h5" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", color: palette.ink, fontSize: '1.45rem' }}>
                                            {selectedUserDetail.displayName || `${selectedUserDetail.firstName || ''} ${selectedUserDetail.lastName || ''}`.trim() || 'User'}
                                        </Typography>
                                        {sentIntroductions[selectedUserDetail.uid || selectedUserDetail.id]?.status && (
                                            <PremiumStatusChip status={sentIntroductions[selectedUserDetail.uid || selectedUserDetail.id].status} />
                                        )}
                                    </Box>
                                    <Box sx={[{ alignItems: 'center', flexWrap: 'wrap', gap: 0.75, display: 'flex' }, { mt: 0.75 }]}>
                                        <Typography variant="body2" sx={{ color: palette.inkFaint, fontSize: '0.85rem' }}>
                                            {selectedUserDetail.companyName || 'N/A'}
                                            {selectedUserDetail.country ? ` • ${selectedUserDetail.country}` : ''}
                                        </Typography>
                                        <Box sx={{ ...glassPill(palette.teal), px: 1.1, height: 22, fontSize: '0.6rem' }}>
                                            {selectedUserDetail.role || 'Member'}
                                        </Box>
                                        <PremiumStatusChip status={selectedUserDetail.isBlocked ? 'blocked' : selectedUserDetail.isActive ? 'active' : 'pending'} />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    {(() => {
                                        const targetId = selectedUserDetail.uid || selectedUserDetail.id;
                                        const introStatus = getIntroStatusForUser(targetId);
                                        const isLocked = introStatus.status === 'pending';
                                        return (
                                            <Tooltip title={isLocked ? 'Available once they accept your introduction' : 'Chat'}>
                                                <span>
                                                    <IconButton
                                                        aria-label="Chat"
                                                        disabled={isLocked}
                                                        onClick={() => { setOpenUserDetailDialog(false); handleOpenChatWithUser(selectedUserDetail); }}
                                                        sx={{ ...glassPod(isLocked ? palette.inkFaint : palette.gold, 38, '12px'), '&:hover': { transform: isLocked ? 'none' : 'scale(1.08)' }, opacity: isLocked ? 0.55 : 1 }}
                                                    >
                                                        {isLocked ? <LockIcon sx={{ fontSize: 17 }} /> : <ChatIcon sx={{ fontSize: 19 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        );
                                    })()}
                                    <Tooltip title="AI Introduction">
                                        <IconButton
                                            aria-label="AI Introduction"
                                            onClick={() => { setOpenUserDetailDialog(false); handleIntroduceUser(selectedUserDetail); }}
                                            sx={{ ...glassPod(palette.purple, 38, '12px'), '&:hover': { transform: 'scale(1.08)' } }}
                                        >
                                            <AiIcon sx={{ fontSize: 19 }} />
                                        </IconButton>
                                    </Tooltip>
                                    {(() => {
                                        const targetId = selectedUserDetail.uid || selectedUserDetail.id;
                                        const introStatus = getIntroStatusForUser(targetId);
                                        const isLocked = introStatus.status === 'pending';
                                        return (
                                            <Tooltip title={isLocked ? 'Available once they accept your introduction' : 'Schedule Meeting'}>
                                                <span>
                                                    <IconButton
                                                        aria-label="Schedule Meeting"
                                                        disabled={isLocked}
                                                        onClick={() => { setOpenUserDetailDialog(false); handleScheduleMeeting(selectedUserDetail); }}
                                                        sx={{ ...glassPod(isLocked ? palette.inkFaint : palette.rust, 38, '12px'), '&:hover': { transform: isLocked ? 'none' : 'scale(1.08)' }, opacity: isLocked ? 0.55 : 1 }}
                                                    >
                                                        {isLocked ? <LockIcon sx={{ fontSize: 17 }} /> : <EventIcon sx={{ fontSize: 19 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        );
                                    })()}
                                </Box>
                            </Box>

                            {getIntroStatusForUser(selectedUserDetail.uid || selectedUserDetail.id).status === 'pending' && (
                                <Paper elevation={0} sx={{ ...glassWell('14px'), p: 1.75, mb: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box sx={{ ...glassPod(palette.gold, 32, '10px') }}>
                                        <LockIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                    <Typography variant="body2" sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>
                                        Your introduction is awaiting a response. Chat and scheduling unlock once they accept.
                                    </Typography>
                                </Paper>
                            )}

                            <Grid container spacing={2.5}>
                                <Grid size={{ xs: 12 }}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.5 }}>
                                        {[
                                            { label: 'Role', value: selectedUserDetail.role || 'N/A', color: palette.teal, icon: <WorkIcon sx={{ fontSize: 16 }} /> },
                                            { label: 'Title', value: selectedUserDetail.businessTitle || selectedUserDetail.title || 'N/A', color: palette.gold, icon: <TitleIcon sx={{ fontSize: 16 }} /> },
                                            { label: 'Specialization', value: asText(selectedUserDetail.specialization) || 'N/A', color: palette.purple, icon: <StarsIcon sx={{ fontSize: 16 }} /> },
                                            { label: 'Service', value: asText(selectedUserDetail.primaryServiceProvided) || 'N/A', color: palette.pine, icon: <DiamondIcon sx={{ fontSize: 16 }} /> },
                                        ].map((tile) => (
                                            <Box key={tile.label} sx={{ ...glassCard({ level: 1, radius: '16px' }), p: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.9 }}>
                                                    <Box sx={glassPod(tile.color, 30, '9px')}>{tile.icon}</Box>
                                                    <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                                                        {tile.label}
                                                    </Typography>
                                                </Box>
                                                <Typography variant="subtitle1" fontWeight={700} sx={{ color: palette.ink, fontSize: '0.88rem', fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", wordBreak: 'break-word' }}>
                                                    {tile.value}
                                                </Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                </Grid>

                                {selectedUserDetail.email && (
                                    <Grid size={{ xs: 12 }}>
                                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={glassPod(palette.teal, 36, '10px')}>
                                                <EmailIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                                                    Email
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: palette.ink, fontSize: '0.85rem' }}>
                                                    {selectedUserDetail.email}
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}

                                {selectedUserDetail.linkedinProfile && (
                                    <Grid size={{ xs: 12 }}>
                                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <Box sx={glassPod(palette.linkedin, 36, '10px')}>
                                                <LinkedInIcon sx={{ fontSize: 18 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>
                                                    LinkedIn
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: palette.ink, fontSize: '0.85rem' }}>
                                                    <a href={safeExternalUrl(selectedUserDetail.linkedinProfile, { allowBlob: false }) || undefined} target="_blank" rel="noopener noreferrer" style={{ color: palette.rust, textDecoration: 'none' }}>
                                                        View Profile
                                                    </a>
                                                </Typography>
                                            </Box>
                                        </Paper>
                                    </Grid>
                                )}

                                {[
                                    {
                                        title: 'Specializations',
                                        items: asList(selectedUserDetail.specialization).map(v => ({ v, c: palette.purple, I: StarsIcon })),
                                    },
                                    {
                                        title: 'Services Provided',
                                        items: [
                                            ...asList(selectedUserDetail.primaryServiceProvided).map(v => ({ v, c: palette.gold, I: DiamondIcon })),
                                            ...asList(selectedUserDetail.secondaryServiceProvided).map(v => ({ v, c: palette.amber, I: DiamondIcon })),
                                        ],
                                    },
                                    {
                                        title: 'Regions Available',
                                        items: [
                                            ...asList(selectedUserDetail.primaryRegionAvailability).map(v => ({ v, c: palette.teal, I: PublicIcon })),
                                            ...asList(selectedUserDetail.secondaryRegionAvailability).map(v => ({ v, c: palette.pine, I: PublicIcon })),
                                        ],
                                    },
                                    {
                                        title: 'Buying Interests',
                                        items: [
                                            ...asList(selectedUserDetail.primaryServiceBuying).map(v => ({ v, c: palette.rust, I: ShoppingCartIcon })),
                                            ...asList(selectedUserDetail.secondaryServiceBuying).map(v => ({ v, c: palette.clay, I: ShoppingCartIcon })),
                                        ],
                                    },
                                ].filter(section => section.items.length > 0).map((section) => (
                                    <Grid key={section.title} size={{ xs: 12 }}>
                                        <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '18px' }), p: 2.5 }}>
                                            <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.inkFaint, mb: 1.5, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                {section.title}
                                            </Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.85 }}>
                                                {section.items.map((item, idx) => (
                                                    <Box key={`${section.title}-${idx}`} sx={{ ...glassPill(item.c), px: 1.3, height: 27, fontSize: '0.66rem' }}>
                                                        <item.I sx={{ fontSize: 12 }} />{item.v}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </DialogContent>

                        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(palette.ink, 0.07)}`, gap: 1.25, flexWrap: 'wrap' }}>
                            <Button onClick={() => setOpenUserDetailDialog(false)} sx={{ ...glassGhostButton(palette.inkFaint), px: 2.25, py: 0.9, fontSize: '0.78rem' }}>
                                Close
                            </Button>
                            {(() => {
                                const targetId = selectedUserDetail.uid || selectedUserDetail.id;
                                const isLocked = getIntroStatusForUser(targetId).status === 'pending';
                                return (
                                    <Tooltip title={isLocked ? 'Available once they accept your introduction' : ''}>
                                        <span>
                                            <Button
                                                startIcon={isLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <ChatIcon sx={{ fontSize: 16 }} />}
                                                disabled={isLocked}
                                                onClick={() => { setOpenUserDetailDialog(false); handleOpenChatWithUser(selectedUserDetail); }}
                                                sx={{ ...glassGhostButton(isLocked ? palette.inkFaint : palette.gold), px: 2.25, py: 0.9, fontSize: '0.78rem' }}
                                            >
                                                Chat
                                            </Button>
                                        </span>
                                    </Tooltip>
                                );
                            })()}
                            <Button
                                startIcon={<AiIcon sx={{ fontSize: 16 }} />}
                                onClick={() => { setOpenUserDetailDialog(false); handleIntroduceUser(selectedUserDetail); }}
                                sx={{ ...glassButton(palette.purple, palette.navy), px: 2.5, py: 0.95, fontSize: '0.78rem' }}
                            >
                                AI Intro
                            </Button>
                            {(() => {
                                const targetId = selectedUserDetail.uid || selectedUserDetail.id;
                                const isLocked = getIntroStatusForUser(targetId).status === 'pending';
                                return (
                                    <Tooltip title={isLocked ? 'Available once they accept your introduction' : ''}>
                                        <span>
                                            <Button
                                                startIcon={isLocked ? <LockIcon sx={{ fontSize: 16 }} /> : <EventIcon sx={{ fontSize: 16 }} />}
                                                disabled={isLocked}
                                                onClick={() => { setOpenUserDetailDialog(false); handleScheduleMeeting(selectedUserDetail); }}
                                                sx={{ ...glassButton(isLocked ? palette.inkFaint : palette.rust, isLocked ? palette.inkFaint : palette.rustDeep), px: 2.5, py: 0.95, fontSize: '0.78rem' }}
                                            >
                                                Schedule
                                            </Button>
                                        </span>
                                    </Tooltip>
                                );
                            })()}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: '100%', ...glassCard({ level: 2, radius: '16px' }) }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CompaniesListing;